import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Card, CardBody, CardHeader, Col, Modal, ModalHeader, Row, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip, Badge, InputGroup, InputGroupText, Input } from "reactstrap";
import Breadcrumbs from '../../../Components/Common/Breadcrumb';
import TableContainer from "Components/Common/TableContainer";
import { set } from "lodash";
import { Info, Edit, Check, X } from "lucide-react";
import ReactMarkdown from 'react-markdown';

export const PackagesMangement = () => {
    const domainId = new URLSearchParams(window.location.search).get("domain");
    const location = useLocation();
    const domainNameFromState = (location.state as { domainName?: string } | null)?.domainName;
    const [domainName, setDomainName] = useState<string | null>(domainNameFromState ?? null);

    // Helper function to format price with commas using built-in toLocaleString
    const formatPrice = (price: string | number | null | undefined): string => {
        if (price == null) return '';
        const num = Number(price);
        return isNaN(num) ? String(price) : num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    };

    const [plans, setPlans] = useState<any[]>([]);
    const [planOptionals, setPlanOptionals] = useState<any[]>([]);
    const [addOns, setAddOns] = useState<any[]>([]);
    const [subscriptionAddOns, setSubscriptionAddOns] = useState<any[]>([]);
    const [oneTimeProducts, setOneTimeProducts] = useState<any[]>([]);

    // Category management
    const [categories, setCategories] = useState<any[]>([]);
    const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>("all");
    // Partner filter
    const [partners, setPartners] = useState<any[]>([]);
    const [selectedPartnerFilter, setSelectedPartnerFilter] = useState<string>("all");

    const partnerPickNote = "Only one partner can be selected per product.";
    const [manageCategoriesModal, setManageCategoriesModal] = useState(false);
    const [categoryDetails, setCategoryDetails] = useState<Record<string, any>>({});
    const [deleteCategoryModal, setDeleteCategoryModal] = useState(false);
    const [managePartnersModal, setManagePartnersModal] = useState(false);

    const getDefaultCategoryId = () => {
        const def = categories.find((c: any) => c?.is_default);
        return def?.id ?? null;
    };

    const extractCategoryIds = (item: any): number[] => {
        const cats = item?.categories;
        if (!Array.isArray(cats)) return [];
        return cats
            .map((c: any) => (typeof c === "object" ? c?.id : c))
            .map((v: any) => Number(v))
            .filter((v: any) => Number.isFinite(v) && v > 0);
    };

    const extractPartnerIds = (item: any): number[] => {
        const ps = item?.partners;
        if (!Array.isArray(ps)) return [];
        return ps
            .map((p: any) => (typeof p === "object" ? p?.id : p))
            .map((v: any) => Number(v))
            .filter((v: any) => Number.isFinite(v) && v > 0);
    };

    const extractPartnersWithShare = (item: any): Array<{partner_id: string, share_percentage: number}> => {
        const ps = item?.partners;
        if (!Array.isArray(ps)) return [];
        // Only return the first partner (enforce single partner selection in UI)
        const partners = ps
            .map((p: any) => {
                if (typeof p === "object" && p?.id) {
                    const id = String(p.id);
                    const share = typeof p.share_percentage === "number" ? p.share_percentage : 0;
                    if (id && Number.isFinite(share)) {
                        return { partner_id: id, share_percentage: share };
                    }
                }
                return null;
            })
            .filter((p): p is {partner_id: string, share_percentage: number} => p !== null);
        // Return only the first partner
        return partners.slice(0, 1);
    };

    const extractParentPlanIds = (item: any): number[] => {
        const parentPlans = item?.parent_plans;
        if (!Array.isArray(parentPlans)) return [];
        return parentPlans
            .map((p: any) => (typeof p === "object" ? p?.id : p))
            .map((v: any) => Number(v))
            .filter((v: any) => Number.isFinite(v) && v > 0);
    };

    const getCategoryLabels = (item: any): string[] => {
        const cats = item?.categories;
        if (!Array.isArray(cats)) return [];
        return cats
            .map((c: any) => {
                if (typeof c === "object" && c?.name) return String(c.name);
                const id = Number(c);
                if (!Number.isFinite(id)) return "";
                const found = categories.find((x: any) => Number(x?.id) === id);
                return found?.name ? String(found.name) : "";
            })
            .map((s: string) => s.trim())
            .filter((s: string) => !!s);
    };

    const getPartnerLabels = (item: any): string[] => {
        const ps = item?.partners;
        if (!Array.isArray(ps)) return [];
        return ps
            .map((p: any) => {
                if (typeof p === "object" && p?.name) return String(p.name);
                const id = typeof p === "object" ? String(p?.id ?? p?.partner_id ?? "") : String(p);
                if (!id) return "";
                const found = partners.find((x: any) => String(x?.id) === id);
                return found?.name ? String(found.name) : "";
            })
            .map((s: string) => s.trim())
            .filter((s: string) => !!s);
    };

    const Chip = ({
        label,
        kind,
    }: {
        label: string;
        kind: "category" | "partner" | "empty";
    }) => {
        const stylesByKind: Record<string, React.CSSProperties> = {
            category: {
                background: "#e3f2fd",
                border: "1px solid #1976d2",
                color: "#0d47a1",
            },
            partner: {
                background: "#e8f5e9",
                border: "1px solid #2e7d32",
                color: "#1b5e20",
            },
            empty: {
                background: "#f5f5f5",
                border: "1px solid #cfcfcf",
                color: "#666",
            },
        };
        return (
            <span
                style={{
                    display: "inline-block",
                    padding: "2px 8px",
                    borderRadius: 999,
                    fontSize: 12,
                    lineHeight: "18px",
                    fontWeight: 600,
                    ...stylesByKind[kind],
                }}
                title={label}
            >
                {label}
            </span>
        );
    };

    const FiltersChips = ({ item }: { item: any }) => {
        const catLabels = getCategoryLabels(item);
        const partnerLabels = getPartnerLabels(item);
        return (
            <div style={{ maxHeight: 84, overflowY: "auto" }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, marginRight: 6 }}>Categories:</span>
                    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6 }}>
                        {catLabels.length > 0
                            ? catLabels.map((name, idx) => <Chip key={`cat-${idx}-${name}`} label={name} kind="category" />)
                            : <Chip label="None" kind="empty" />}
                    </span>
                </div>
                <div style={{ fontSize: 12, color: "#666" }}>
                    <span style={{ fontWeight: 700, marginRight: 6 }}>Partners:</span>
                    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6 }}>
                        {partnerLabels.length > 0
                            ? partnerLabels.map((name, idx) => <Chip key={`prt-${idx}-${name}`} label={name} kind="partner" />)
                            : <Chip label="Unassigned" kind="empty" />}
                    </span>
                </div>
            </div>
        );
    };

    const CategoryMultiSelect = ({
        selectedIds,
        onChange,
        maxVisibleItems = 6,
    }: {
        selectedIds: number[];
        onChange: (ids: number[]) => void;
        maxVisibleItems?: number;
    }) => {
        const normalizedSelected = (selectedIds || [])
            .map((v) => Number(v))
            .filter((v) => Number.isFinite(v) && v > 0);

        const rowHeightPx = 44; // approx height per item row
        const listHeightPx = Math.max(1, maxVisibleItems) * rowHeightPx;

        // UI enforcement: must always have at least one category selected.
        // If none are selected (e.g., older data or categories loaded after state init),
        // auto-select Default (preferred) or the first available category.
        useEffect(() => {
            if (normalizedSelected.length > 0) return;
            if (!categories || categories.length === 0) return;
            const defaultCat = categories.find((c: any) => !!c?.is_default);
            const fallback = defaultCat ?? categories[0];
            const id = Number(fallback?.id);
            if (Number.isFinite(id) && id > 0) {
                onChange([id]);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [categories?.length]);

        const toggle = (id: number) => {
            const exists = normalizedSelected.includes(id);
            if (exists) {
                onChange(normalizedSelected.filter((x) => x !== id));
            } else {
                onChange([...normalizedSelected, id]);
            }
        };

        return (
            <div
                style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 8,
                    padding: 10,
                    height: listHeightPx,
                    overflowY: "auto",
                    background: "#fff",
                }}
            >
                {categories.length === 0 && (
                    <div className="text-muted" style={{ fontSize: 13 }}>
                        No categories found.
                    </div>
                )}
                {categories.map((cat: any) => {
                    const id = Number(cat.id);
                    const checked = normalizedSelected.includes(id);
                    const isOnlySelected = checked && normalizedSelected.length === 1;
                    const disableUncheckLast = isOnlySelected; // always enforce >= 1 selection

                    return (
                        <label
                            key={cat.id}
                            style={{
                                display: "flex",
                                gap: 10,
                                alignItems: "flex-start",
                                padding: "6px 4px",
                                cursor: disableUncheckLast ? "not-allowed" : "pointer",
                                opacity: disableUncheckLast ? 0.7 : 1,
                                userSelect: "none",
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={checked}
                                disabled={disableUncheckLast}
                                onChange={() => toggle(id)}
                                style={{ marginTop: 3 }}
                            />
                            <div style={{ lineHeight: 1.2 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>
                                    {cat.name}
                                    {cat.is_default ? " (Default)" : ""}
                                </div>
                                {!!cat.description && (
                                    <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                                        {cat.description}
                                    </div>
                                )}
                            </div>
                        </label>
                    );
                })}
            </div>
        );
    };

    const PlanMultiSelect = ({
        selectedIds,
        onChange,
        maxVisibleItems = 6,
    }: {
        selectedIds: number[];
        onChange: (ids: number[]) => void;
        maxVisibleItems?: number;
    }) => {
        const normalizedSelected = (selectedIds || [])
            .map((v) => Number(v))
            .filter((v) => Number.isFinite(v) && v > 0);

        const rowHeightPx = 44;
        const listHeightPx = Math.max(1, maxVisibleItems) * rowHeightPx;

        const toggle = (id: number) => {
            const exists = normalizedSelected.includes(id);
            if (exists) {
                onChange(normalizedSelected.filter((x) => x !== id));
            } else {
                onChange([...normalizedSelected, id]);
            }
        };

        return (
            <div
                style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 8,
                    padding: 10,
                    height: listHeightPx,
                    overflowY: "auto",
                    background: "#fff",
                }}
            >
                {plans.length === 0 && (
                    <div className="text-muted" style={{ fontSize: 13 }}>
                        No plans found.
                    </div>
                )}
                {plans.map((plan: any) => {
                    const id = Number(plan.id);
                    const checked = normalizedSelected.includes(id);

                    return (
                        <label
                            key={plan.id}
                            style={{
                                display: "flex",
                                gap: 10,
                                alignItems: "flex-start",
                                padding: "6px 4px",
                                cursor: "pointer",
                                userSelect: "none",
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(id)}
                                style={{ marginTop: 3 }}
                            />
                            <div style={{ lineHeight: 1.2 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>
                                    {plan.title}
                                </div>
                                {plan.price !== null && plan.price !== undefined && (
                                    <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                                        ${formatPrice(plan.price)}
                                        {plan.validity && plan.validity !== '' && (
                                            <span> / {plan.validity}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </label>
                    );
                })}
            </div>
        );
    };

    const PartnerMultiSelect = ({
        selectedPartners,
        onChange,
        maxVisibleItems = 4,
    }: {
        selectedPartners: Array<{partner_id: string, share_percentage: number}>;
        onChange: (partners: Array<{partner_id: string, share_percentage: number}>) => void;
        maxVisibleItems?: number;
    }) => {
        const [editingId, setEditingId] = useState<string | null>(null);
        const [editValue, setEditValue] = useState<string>("");

        // Normalize selected partners - only take the first one (enforce single selection)
        const normalizedSelected = (selectedPartners || [])
            .filter((p) => p && typeof p.partner_id === 'string' && p.partner_id)
            .slice(0, 1); // Only keep the first partner

        const selectedIds = normalizedSelected.map((p) => p.partner_id);
        const selectedId = selectedIds.length > 0 ? selectedIds[0] : null;

        const rowHeightPx = 50; // height per item row
        const headerHeightPx = 40;
        const listHeightPx = Math.max(1, maxVisibleItems) * rowHeightPx + headerHeightPx;

        // Get partner by ID
        const getPartnerById = (id: string) => {
            return partners.find((p: any) => String(p.id) === id);
        };

        // Get share percentage for a partner
        const getSharePercentage = (id: string): number => {
            const found = normalizedSelected.find((p) => p.partner_id === id);
            return found ? found.share_percentage : 0;
        };

        // Toggle partner selection - only allow one partner
        const toggle = (id: string) => {
            if (selectedId === id) {
                // Deselect if clicking the same partner
                onChange([]);
            } else {
                // Select new partner (replace any existing selection)
                // Get share percentage from existing selection if available, otherwise default to 0
                const existingShare = normalizedSelected.find((p) => p.partner_id === id)?.share_percentage || 0.0;
                onChange([{ partner_id: id, share_percentage: existingShare }]);
            }
        };

        // Start editing share percentage
        const startEdit = (id: string) => {
            setEditingId(id);
            setEditValue(getSharePercentage(id).toString());
        };

        // Save edited share percentage
        const saveEdit = () => {
            if (editingId === null) return;
            
            const value = parseFloat(editValue);
            if (isNaN(value) || value < 0 || value > 100) {
                // Invalid value, cancel edit
                setEditingId(null);
                return;
            }

            // Simply update the share percentage for this partner (no redistribution needed)
            const updated = normalizedSelected.map((p) => 
                p.partner_id === editingId 
                    ? { ...p, share_percentage: value }
                    : p
            );
            onChange(updated);
            
            setEditingId(null);
        };

        // Cancel editing
        const cancelEdit = () => {
            setEditingId(null);
            setEditValue("");
        };

        // Handle Enter key in input
        const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
            if (e.key === 'Enter') {
                saveEdit();
            } else if (e.key === 'Escape') {
                cancelEdit();
            }
        };

        // Get selected partners data for display
        const selectedPartnersData = partners
            .filter((p: any) => selectedIds.includes(String(p.id)))
            .map((p: any) => ({
                id: String(p.id),
                name: p?.name || `Partner #${p.id}`,
                share_percentage: getSharePercentage(String(p.id))
            }));

        return (
            <div
                style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 8,
                    overflow: "hidden",
                    background: "#fff",
                }}
            >
                {partners.length === 0 ? (
                    <div className="text-muted" style={{ fontSize: 13, padding: 20, textAlign: "center" }}>
                        No partners found for this domain.
                    </div>
                ) : (
                    <>
                        <div style={{ maxHeight: listHeightPx, overflowY: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ background: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                                        <th style={{ padding: "10px 8px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#666", width: "40px" }}>
                                            {/* Radio button header - no select all for single selection */}
                                        </th>
                                        <th style={{ padding: "10px 8px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#666" }}>
                                            Partner Name
                                        </th>
                                        <th style={{ padding: "10px 8px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#666", width: "150px" }}>
                                            Share %
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* None (Unassigned) row - allows admin to deselect partner and make plan/product orphan */}
                                    <tr
                                        key="none"
                                        style={{
                                            borderBottom: "1px solid #e0e0e0",
                                            cursor: "pointer",
                                            background: selectedIds.length === 0 ? "#e8f4f8" : undefined,
                                        }}
                                        onClick={() => onChange([])}
                                    >
                                        <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                                            <input
                                                type="radio"
                                                name="partner-selection"
                                                checked={selectedIds.length === 0}
                                                onChange={() => onChange([])}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ cursor: "pointer" }}
                                            />
                                        </td>
                                        <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                                            <div style={{ fontWeight: 600, fontSize: 13, color: "#666" }}>
                                                None (Unassigned)
                                            </div>
                                        </td>
                                        <td style={{ padding: "10px 8px", verticalAlign: "middle" }} />
                                    </tr>
                                    {partners.map((p: any) => {
                                        const id = String(p.id);
                                        const checked = selectedId === id;
                                        const label = p?.name || `Partner #${p.id}`;
                                        const sharePercentage = getSharePercentage(id);
                                        const isEditing = editingId === id;

                                        // Only show share percentage column content if partner is selected
                                        if (!checked) {
                                            return (
                                                <tr
                                                    key={p.id}
                                                    style={{
                                                        borderBottom: "1px solid #e0e0e0",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                                                        <input
                                                            type="radio"
                                                            name="partner-selection"
                                                            checked={false}
                                                            onChange={() => toggle(id)}
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                                                        <div style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>
                                                            {label}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                                                        {/* Empty when not selected */}
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        return (
                                            <tr
                                                key={p.id}
                                                style={{
                                                    borderBottom: "1px solid #e0e0e0",
                                                    cursor: "pointer",
                                                }}
                                                onClick={(e) => {
                                                    // Don't toggle if clicking on input or buttons
                                                    if ((e.target as HTMLElement).tagName === 'INPUT' || 
                                                        (e.target as HTMLElement).tagName === 'BUTTON' ||
                                                        (e.target as HTMLElement).closest('button') ||
                                                        (e.target as HTMLElement).closest('input')) {
                                                        return;
                                                    }
                                                }}
                                            >
                                                <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                                                    <input
                                                        type="radio"
                                                        name="partner-selection"
                                                        checked={checked}
                                                        onChange={() => toggle(id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ cursor: "pointer" }}
                                                    />
                                                </td>
                                                <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>
                                                        {label}
                                                    </div>
                                                </td>
                                                <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                                                    {isEditing ? (
                                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                step="0.01"
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onKeyDown={(e) => handleKeyDown(e, id)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{
                                                                    width: "70px",
                                                                    padding: "4px 8px",
                                                                    border: "1px solid #1976d2",
                                                                    borderRadius: 4,
                                                                    fontSize: 13,
                                                                }}
                                                                autoFocus
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    saveEdit();
                                                                }}
                                                                style={{
                                                                    background: "none",
                                                                    border: "none",
                                                                    cursor: "pointer",
                                                                    padding: 4,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    color: "#2e7d32",
                                                                }}
                                                                title="Save"
                                                            >
                                                                <Check size={16} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    cancelEdit();
                                                                }}
                                                                style={{
                                                                    background: "none",
                                                                    border: "none",
                                                                    cursor: "pointer",
                                                                    padding: 4,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    color: "#d32f2f",
                                                                }}
                                                                title="Cancel"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                            <span style={{ fontSize: 13, color: "#222", minWidth: "50px" }}>
                                                                {sharePercentage.toFixed(2)}%
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    startEdit(id);
                                                                }}
                                                                style={{
                                                                    background: "none",
                                                                    border: "none",
                                                                    cursor: "pointer",
                                                                    padding: 4,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    color: "#1976d2",
                                                                }}
                                                                title="Edit share percentage"
                                                            >
                                                                <Edit size={14} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const [planDetails, setPlanDetails] = useState<Record<string, any>>({});
    const [addOnDetails, setAddOnDetails] = useState<Record<string, any>>({});
    const [subscriptionAddOnDetails, setSubscriptionAddOnDetails] = useState<Record<string, any>>({});
    const [oneTimeProductDetails, setOneTimeProductDetails] = useState<Record<string, any>>({});

    // Add a separate state for features input
    const [featuresInput, setFeaturesInput] = useState<string>("");
    const [addOnFeaturesInput, setAddOnFeaturesInput] = useState<string>("");
    const [subscriptionAddOnFeaturesInput, setSubscriptionAddOnFeaturesInput] = useState<string>("");
    const [planOptionalFeaturesInput, setPlanOptionalFeaturesInput] = useState<string>("");
    const [oneTimeProductFeaturesInput, setOneTimeProductFeaturesInput] = useState<string>("");

    // Stripe mode check states
    const [stripeCheckResults, setStripeCheckResults] = useState<Record<string, any>>({});
    const [checkingStripe, setCheckingStripe] = useState<Record<string, boolean>>({});

    const [addPlanModal, setAddPlanModal] = useState(false);
    const [deletePlanModal, setDeletePlanModal] = useState(false);

    // --- Plan Optionals Management ---
    const [planOptionalModal, setPlanOptionalModal] = useState(false);
    const [deletePlanOptionalModal, setDeletePlanOptionalModal] = useState(false);
    const [planOptionalDetails, setPlanOptionalDetails] = useState<Record<string, any>>({});
    const [planOptionalGroupInput, setPlanOptionalGroupInput] = useState("");
    const [isNewGroup, setIsNewGroup] = useState(false);

    // Navigation state for plans and plan optionals
    const [plansStartIndex, setPlansStartIndex] = useState(0);
    const [planOptionalsStartIndex, setPlanOptionalsStartIndex] = useState(0);
    const PLANS_VISIBLE_COUNT = 3;
    const PLAN_OPTIONALS_VISIBLE_COUNT = 4;

    const planOptionalGroups = Array.from(new Set(planOptionals.map((o: any) => o.group)));

    const togglePlanOptionalModal = () => {
        // Only reset form state when closing the modal.
        // When opening for edit, callers set `planOptionalDetails` + `planOptionalGroupInput` first.
        setPlanOptionalModal((prev) => {
            if (prev) {
                setIsNewGroup(false);
                setPlanOptionalGroupInput("");
                setPlanOptionalFeaturesInput("");
            }
            return !prev;
        });
    };
    const toggleDeletePlanOptionalModal = () => setDeletePlanOptionalModal(!deletePlanOptionalModal);

    // Navigation functions for plans
    const navigatePlansLeft = () => {
        setPlansStartIndex(Math.max(0, plansStartIndex - 1));
    };
    const navigatePlansRight = () => {
        const maxStartIndex = Math.max(0, plans.length - PLANS_VISIBLE_COUNT);
        setPlansStartIndex(Math.min(maxStartIndex, plansStartIndex + 1));
    };
    const getVisiblePlans = () => {
        if (plans.length <= PLANS_VISIBLE_COUNT) {
            return plans;
        }
        return plans.slice(plansStartIndex, plansStartIndex + PLANS_VISIBLE_COUNT);
    };

    // Navigation functions for plan optionals
    const navigatePlanOptionalsLeft = () => {
        setPlanOptionalsStartIndex(Math.max(0, planOptionalsStartIndex - 1));
    };
    const navigatePlanOptionalsRight = () => {
        const maxStartIndex = Math.max(0, planOptionalGroups.length - PLAN_OPTIONALS_VISIBLE_COUNT);
        setPlanOptionalsStartIndex(Math.min(maxStartIndex, planOptionalsStartIndex + 1));
    };
    const getVisiblePlanOptionalGroups = () => {
        if (planOptionalGroups.length <= PLAN_OPTIONALS_VISIBLE_COUNT) {
            return planOptionalGroups;
        }
        return planOptionalGroups.slice(planOptionalsStartIndex, planOptionalsStartIndex + PLAN_OPTIONALS_VISIBLE_COUNT);
    };

    // --- Add-ons Management ---
    const [addOnModal, setAddOnModal] = useState(false);
    const [deleteAddOnModal, setDeleteAddOnModal] = useState(false);

    const toggleAddOnModal = () => {
        if (addOnModal) {
            setAddOnFeaturesInput("");
        }
        setAddOnModal(!addOnModal);
    };
    const toggleDeleteAddOnModal = () => setDeleteAddOnModal(!deleteAddOnModal);

    // --- Subscription Add-ons Management ---
    const [subscriptionAddOnModal, setSubscriptionAddOnModal] = useState(false);
    const [deleteSubscriptionAddOnModal, setDeleteSubscriptionAddOnModal] = useState(false);

    const toggleSubscriptionAddOnModal = () => {
      if (subscriptionAddOnModal) {
        setSubscriptionAddOnFeaturesInput("");
      }
      setSubscriptionAddOnModal(!subscriptionAddOnModal);
    };
    const toggleDeleteSubscriptionAddOnModal = () => setDeleteSubscriptionAddOnModal(!deleteSubscriptionAddOnModal);

    // --- One-Time Products Management ---
    const [oneTimeProductModal, setOneTimeProductModal] = useState(false);
    const [deleteOneTimeProductModal, setDeleteOneTimeProductModal] = useState(false);

    const toggleOneTimeProductModal = () => {
        if (oneTimeProductModal) {
            setOneTimeProductFeaturesInput("");
        }
        setOneTimeProductModal(!oneTimeProductModal);
    };
    const toggleDeleteOneTimeProductModal = () => setDeleteOneTimeProductModal(!deleteOneTimeProductModal);

    // --- Product Filters (Category assignment) ---
    const [productFiltersModal, setProductFiltersModal] = useState(false);
    const [productFiltersDetails, setProductFiltersDetails] = useState<{
        product_type?: string;
        product_id?: number;
        title?: string;
        categories: number[];
        partners: Array<{partner_id: string, share_percentage: number}>;
    }>({ categories: [], partners: [] });

    const toggleProductFiltersModal = () => setProductFiltersModal(!productFiltersModal);

    const openProductFiltersModal = (product: any, product_type: string) => {
        if (!product?.id) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please save the product first before editing filters.", type: "error" });
            return;
        }
        // Check if partners are already in extracted format (has partner_id) or need extraction (has id)
        let partners: Array<{partner_id: string, share_percentage: number}>;
        if (product.partners && Array.isArray(product.partners) && product.partners.length > 0) {
            // Check if already in extracted format (has partner_id)
            if (product.partners[0]?.partner_id !== undefined) {
                partners = product.partners as Array<{partner_id: string, share_percentage: number}>;
            } else {
                // Extract from API format (has id)
                partners = extractPartnersWithShare(product);
            }
        } else {
            // Extract from product object
            partners = extractPartnersWithShare(product);
        }
        setProductFiltersDetails({
            product_type,
            product_id: Number(product.id),
            title: product?.title ?? "",
            categories: extractCategoryIds(product),
            partners: partners,
        });
        setProductFiltersModal(true);
    };

    const saveProductFilters = async () => {
        if (!productFiltersDetails?.product_type || !productFiltersDetails?.product_id) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Product information is missing.", type: "error" });
            return;
        }
        try {
            setshowloader(true);
            const response = await axios.post(
                "/payments/updateProductFilters/",
                {
                    product_type: productFiltersDetails.product_type,
                    product_id: productFiltersDetails.product_id,
                    categories: productFiltersDetails.categories || [],
                    partners: productFiltersDetails.partners || [],
                },
                { withCredentials: true }
            );
            if (response && response.status === "UPDATED") {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Filters updated successfully.", type: "success" });
                fetchPlans();
                setProductFiltersModal(false);
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to update filters. Please try again.", type: "error" });
            }
            setshowloader(false);
        } catch (error: any) {
            const errorMsg = error?.response?.data?.error || "Unable to update filters. Please try again.";
            setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
            setshowloader(false);
        }
    };

    const toggleManageCategoriesModal = () => setManageCategoriesModal(!manageCategoriesModal);
    const toggleDeleteCategoryModal = () => setDeleteCategoryModal(!deleteCategoryModal);

    const upsertCategory = async () => {
        if (!domainId) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Domain information is missing.", type: "error" });
            return;
        }
        if (!categoryDetails?.name || !String(categoryDetails.name).trim()) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Category name is required.", type: "error" });
            return;
        }
        const payload: any = {
            domain: domainId,
            name: String(categoryDetails.name).trim(),
            description: categoryDetails.description ?? "",
        };
        if (categoryDetails.id) {
            payload.category_id = categoryDetails.id;
        }
        try {
            const response = await axios.post(
                "/payments/manageCategory/",
                payload,
                { withCredentials: true }
            );
            if (response && (response.status === "CREATED" || response.status === "UPDATED")) {
                setAlertMsgg({ ...alertMsgg, open: true, content: response.status === "CREATED" ? "Category created successfully." : "Category updated successfully.", type: "success" });
                setCategoryDetails({});
                fetchCategories();
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to save the category. Please try again.", type: "error" });
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.error || "Unable to save the category. Please try again.";
            setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
        }
    };

    const deleteCategory = async () => {
        if (!domainId) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Domain information is missing.", type: "error" });
            return;
        }
        if (!categoryDetails?.id) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "No category selected.", type: "error" });
            return;
        }
        try {
            const response = await axios.post(
                "/payments/manageCategory/",
                { domain: domainId, category_id: categoryDetails.id, todelete: true },
                { withCredentials: true }
            );
            if (response && response.status === "DELETED") {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Category deleted successfully.", type: "success" });
                setCategoryDetails({});
                fetchCategories();
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to delete the category. Please try again.", type: "error" });
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.error || "Unable to delete the category. Please try again.";
            setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
        }
    };

    const moveCategoryPriority = async (categoryId: number, direction: 'up' | 'down') => {
        if (!domainId) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Domain information is missing.", type: "error" });
            return;
        }
        try {
            const response = await axios.post(
                "/payments/moveCategoryPriority/",
                { domain: domainId, category_id: categoryId, direction },
                { withCredentials: true }
            );
            if (response && (response.status === "MOVED" || response.status === "NO_CHANGE")) {
                if (response.status === "NO_CHANGE" && response.message) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: response.message, type: "info" });
                }
                fetchCategories();
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to change category priority. Please try again.", type: "error" });
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.error || "Unable to change category priority. Please try again.";
            setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
        }
    };

    const movePartnerPriority = async (partnerId: string, direction: 'up' | 'down') => {
        if (!domainId) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Domain information is missing.", type: "error" });
            return;
        }
        try {
            setshowloader(true);
            const response = await axios.post(
                "/payments/movePartnerPriority/",
                { domain: domainId, partner_id: partnerId, direction },
                { withCredentials: true }
            );
            if (response && (response.status === "MOVED" || response.status === "NO_CHANGE")) {
                if (response.status === "NO_CHANGE" && response.message) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: response.message, type: "info" });
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Partner priority updated successfully.", type: "success" });
                }
                fetchPartners();
                fetchPlans();
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to change partner priority. Please try again.", type: "error" });
            }
            setshowloader(false);
        } catch (error: any) {
            const errorMsg = error?.response?.data?.error || "Unable to change partner priority. Please try again.";
            setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
            setshowloader(false);
        }
    };

    const toggleManagePartnersModal = () => setManagePartnersModal(!managePartnersModal);

    // Fetch domain name when not passed via navigation state (e.g. direct URL or refresh)
    const fetchDomainName = async () => {
        if (!domainId) return;
        try {
            const response = await axios.post("accounts/handleadmdomains", { render: true }, { withCredentials: true });
            if (response.status === "SENT" && Array.isArray(response.domains)) {
                const domain = response.domains.find((d: any) => String(d?.id) === String(domainId));
                if (domain?.name) setDomainName(String(domain.name));
            }
        } catch {
            // Non-blocking: domain name is for display only
        }
    };

    // Helper function to clean markdown content
    const cleanMarkdown = (text: string | null | undefined): string => {
        if (!text) return '';
        // Convert single newlines to double newlines for markdown paragraph breaks
        return String(text).replace(/\n/g, '\n\n');
    };

    const [showloader, setshowloader] = useState(true);
    const [alertMsgg, setAlertMsgg] = useState<any>({ open: false, type: "success", content: "Messages here.", time: "2500" });
    const handleAlertMsggClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") { return; }
        setAlertMsgg({ ...alertMsgg, open: false });
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(
                "/payments/listDomainCategories",
                {
                    params: { "domain": domainId }
                },
                { withCredentials: true }
            );
            if (response && response.status === "SENT") {
                const cats = response.categories || [];
                setCategories(cats);
                // If categories were empty before but now have items, default category was auto-created
                if (cats.length === 0) {
                    setAlertMsgg({ 
                        ...alertMsgg, 
                        open: true, 
                        content: "No product categories found for this domain. Attempting to create default category...", 
                        type: "warning" 
                    });
                    // Retry fetching categories after a short delay (default category should be auto-created)
                    setTimeout(() => {
                        fetchCategories();
                    }, 1000);
                }
            }
        } catch (error: any) {
            console.error("Error fetching categories:", error);
            if (error.response) {
                const errorMsg = error.response.data?.error || "Unable to load categories. Please refresh the page.";
                // Only show error if it's not a 404 (domain might not have categories yet)
                if (error.response.status !== 404) {
                    setAlertMsgg({ 
                        ...alertMsgg, 
                        open: true, 
                        content: errorMsg, 
                        type: "error" 
                    });
                }
            }
        }
    }

    const fetchPartners = async () => {
        try {
            const response = await axios.get(
                "/payments/listDomainPartners",
                { params: { "domain": domainId }, withCredentials: true }
            );
            if (response && response.status === "SENT") {
                setPartners(response.partners || []);
            }
        } catch (error: any) {
            // Non-blocking: partner filter is optional
            console.error("Error fetching partners:", error);
        }
    };

    const fetchPlans = async (categoryOverride?: string, partnerOverride?: string) => {
        try {
            setshowloader(true);
            const params: any = {
                "domain": domainId,
                "category": categoryOverride ?? selectedCategoryFilter,
                "partner": partnerOverride ?? selectedPartnerFilter,
            };
            
            const response = await axios.get(
                "/payments/listDomainProducts",
                { params },
                { withCredentials: true }
            );
            if (response && response.status === "SENT") {
                setPlans(response.Plan)
                setAddOns(response.AddOn)
                setSubscriptionAddOns(response.SubscriptionAddOn)
                setPlanOptionals(response.PlanOptionals)
                setOneTimeProducts(response.OneTimeProduct || [])
            } else {
                console.log(response);
                
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    const errorMsg = error.response.data?.error || "Unable to load products. Please check your input and try again.";
                    setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Your session has expired. Please log in again.", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else if (error.response.status === 404) {
                    const errorMsg = error.response.data?.error || "The requested information could not be found.";
                    setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to load products. Please try again later.", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to connect to the server. Please check your internet connection.", type: "error" });
            }
            setshowloader(false);
        }
    }

    const movePriority = async (productType: string, productId: string, direction: 'up' | 'down') => {
        try {
            setshowloader(true);
            const response = await axios.post(
                "/payments/moveProductPriority/",
                {
                    product_type: productType,
                    product_id: productId,
                    direction: direction,
                    domain: domainId
                },
                { withCredentials: true }
            );
            if (response && (response.status === "MOVED" || response.status === "NO_CHANGE")) {
                if (response.status === "NO_CHANGE") {
                    setAlertMsgg({ ...alertMsgg, open: true, content: response.message, type: "info" });
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: response.message, type: "success" });
                }
                fetchPlans();
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to change product priority. Please try again.", type: "error" });
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                const errorMsg = error.response.data?.error || "Unable to change product priority. Please try again.";
                setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to connect to the server. Please check your internet connection.", type: "error" });
            }
            setshowloader(false);
        }
    }

    const handlePlan = async (operation: string) => {
        // For create/update, ensure required fields are present
        if (
            (operation === "create" || operation === "update") &&
            (!planDetails.title || !planDetails.title.trim())
        ) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter a plan title.", type: "error" });
            return false;
        }
        if ((operation === "create" || operation === "update") && planDetails.pricing_type === 'fixed' && (planDetails.price === '' || planDetails.price === null || planDetails.price === undefined)) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter a price for fixed pricing.", type: "error" });
            return false;
        }
        if ((operation === "create" || operation === "update") && planDetails.pricing_type === 'external' && (!planDetails.external_url || !planDetails.external_url.trim())) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter an external URL for buy externally.", type: "error" });
            return false;
        }

        // For delete, ensure plan_id is present
        if ((operation === "delete" || operation === "update") && !planDetails["id"]) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "No plan selected for deletion.", type: "error" });
            return false;
        }

        // Prepare payload
        let payload: any = {};
        if (operation === "create") {
            // Handle price: null for contact, null for external if empty/0, otherwise use value
            let priceValue = null;
            if (planDetails.pricing_type === 'fixed') {
                priceValue = planDetails.price === '' || planDetails.price === null || planDetails.price === undefined ? null : planDetails.price;
            } else if (planDetails.pricing_type === 'external') {
                // For external, price is optional - set to null if empty, 0, or not provided
                priceValue = (planDetails.price === '' || planDetails.price === null || planDetails.price === undefined || planDetails.price === 0) ? null : planDetails.price;
            }
            
            payload = {
                product_type: 'plan',
                domain: domainId,
                ...planDetails,
                pricing_type: planDetails.pricing_type || 'fixed',
                price: priceValue,
                external_url: planDetails.pricing_type === 'external' ? (planDetails.external_url || '') : null,
                // Explicitly include validity (even if empty) so backend knows it's one-time purchase
                validity: planDetails.validity !== undefined ? planDetails.validity : '',
                // Include validity_time if validity is custom
                ...(planDetails.validity === 'custom' && planDetails.validity_time ? { validity_time: planDetails.validity_time } : {})
            };
        } else if (operation === "update") {
            const { categories: _categories, partners: _partners, ...planDetailsWithoutCategories } = planDetails || {};
            
            // Handle price: null for contact, null for external if empty/0, otherwise use value
            let priceValue = null;
            if (planDetails.pricing_type === 'fixed') {
                priceValue = planDetails.price === '' || planDetails.price === null || planDetails.price === undefined ? null : planDetails.price;
            } else if (planDetails.pricing_type === 'external') {
                // For external, price is optional - set to null if empty, 0, or not provided
                priceValue = (planDetails.price === '' || planDetails.price === null || planDetails.price === undefined || planDetails.price === 0) ? null : planDetails.price;
            }
            
            payload = {
                product_type: 'plan',
                product_id: planDetails.id,
                ...planDetailsWithoutCategories,
                pricing_type: planDetails.pricing_type || 'fixed',
                price: priceValue,
                external_url: planDetails.pricing_type === 'external' ? (planDetails.external_url || '') : null,
                // Explicitly include validity to ensure it's sent (even if empty)
                validity: planDetails.validity !== undefined ? planDetails.validity : '',
                // Include validity_time if validity is custom
                ...(planDetails.validity === 'custom' && planDetails.validity_time ? { validity_time: planDetails.validity_time } : {})
            };
        } else if (operation === "delete") {
            payload = {
                product_type: 'plan',
                product_id: planDetails.id,

                todelete: true
            };
        }

        try {
            setshowloader(true);
            const response = await axios.post(
                "/payments/manageProduct/",
                payload,
                { withCredentials: true }
            );
            if (response && (response.status === "CREATED" || response.status === "VERSIONED" || response.status === "ARCHIVED" || response.status === "UPDATED" || response.status === "DELETED")) {
                let message = `Plan ${operation}d successfully.`;
                if (response.status === "VERSIONED") {
                    message = "Your changes have been saved successfully.";
                } else if (response.status === "ARCHIVED") {
                    message = "Plan archived successfully.";
                }
                setAlertMsgg({ ...alertMsgg, open: true, content: message, type: "success" });
                fetchPlans();
                setshowloader(false);
                return true;
            } else {
                const action = operation === "create" ? "create" : operation === "update" ? "update" : "delete";
                setAlertMsgg({ ...alertMsgg, open: true, content: `Unable to ${action} the plan. Please try again.`, type: "error" });
            }
            setshowloader(false);
        } catch (error: any) {
            console.log(error);

            if (error.response) {
                const errorMsg = error.response.data?.error || "Unable to process the request. Please check all fields and try again.";
                setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to connect to the server. Please check your internet connection.", type: "error" });
            }
            setshowloader(false);
        }
        return false;
    }

    // TODO: handle add/edit plan optional (API call)
    const handlePlanOptional = async (operation: string) => {
        // For create/update, ensure required fields are present
        if (
            (operation === "create" || operation === "update") &&
            (!planOptionalDetails.title || !planOptionalDetails.title.trim())
        ) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter optional title.", type: "error" });
            return false;
        }
        if ((operation === "create" || operation === "update") && (!planOptionalDetails.description || !planOptionalDetails.description.trim())) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter a description.", type: "error" });
            return false;
        }

        // For delete, ensure optional id is present
        if ((operation === "delete" || operation === "update") && !planOptionalDetails["id"]) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "No optional selected for deletion.", type: "error" });
            return false;
        }

        // Prepare payload
        let payload: any = {};
        if (operation === "create") {
            payload = {
                product_type: 'planoptionals',
                domain: domainId,
                features: planOptionalDetails.features || [],
                ...planOptionalDetails,
                pricing_type: 'fixed', // Plan optionals always use fixed pricing
                price: planOptionalDetails.price === '' ? null : planOptionalDetails.price,
                external_url: null, // Plan optionals don't support external links
                // Explicitly include validity (even if empty)
                validity: planOptionalDetails.validity !== undefined ? planOptionalDetails.validity : '',
                ...(planOptionalDetails.validity === 'custom' && planOptionalDetails.validity_time ? { validity_time: planOptionalDetails.validity_time } : {}),
                parent_plans: planOptionalDetails.parent_plans || []
            };
        } else if (operation === "update") {
            const { categories: _categories, partners: _partners, parent_plans: _parent_plans, ...optionalWithoutFilters } = planOptionalDetails || {};
            payload = {
                product_type: 'planoptionals',
                product_id: planOptionalDetails.id,
                ...optionalWithoutFilters,
                pricing_type: 'fixed', // Plan optionals always use fixed pricing
                price: planOptionalDetails.price === '' ? null : planOptionalDetails.price,
                external_url: null, // Plan optionals don't support external links
                // Explicitly include validity to ensure it's sent (even if empty)
                validity: planOptionalDetails.validity !== undefined ? planOptionalDetails.validity : '',
                ...(planOptionalDetails.validity === 'custom' && planOptionalDetails.validity_time ? { validity_time: planOptionalDetails.validity_time } : {}),
                parent_plans: planOptionalDetails.parent_plans || []
            };
        } else if (operation === "delete") {
            payload = {
                product_type: 'planoptionals',
                product_id: planOptionalDetails.id,

                todelete: true
            };
        }

        try {
            setshowloader(true);
            const response = await axios.post(
                "/payments/manageProduct/",
                payload,
                { withCredentials: true }
            );
            if (response && (response.status === "CREATED" || response.status === "VERSIONED" || response.status === "ARCHIVED" || response.status === "UPDATED" || response.status === "DELETED")) {
                let message = `Plan optional ${operation}d successfully.`;
                if (response.status === "VERSIONED") {
                    message = "Your changes have been saved successfully.";
                } else if (response.status === "ARCHIVED") {
                    message = "Plan optional archived successfully.";
                }
                setAlertMsgg({ ...alertMsgg, open: true, content: message, type: "success" });
                fetchPlans();
                setshowloader(false);
                return true;
            } else {
                const action = operation === "create" ? "create" : operation === "update" ? "update" : "delete";
                setAlertMsgg({ ...alertMsgg, open: true, content: `Unable to ${action} the plan optional. Please try again.`, type: "error" });
            }
            setshowloader(false);
        } catch (error: any) {
            console.log(error);

            if (error.response) {
                const errorMsg = error.response.data?.error || "Unable to process the request. Please check all fields and try again.";
                setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to connect to the server. Please check your internet connection.", type: "error" });
            }
            setshowloader(false);
        }
        return false;
    }

    const handleAddOn = async (operation: string) => {
        if ((operation === "create" || operation === "update") && (!addOnDetails.title || !addOnDetails.title.trim())) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter add-on title.", type: "error" });
            return false;
        }
        if ((operation === "create" || operation === "update") && (addOnDetails.price === '' || addOnDetails.price === null || addOnDetails.price === undefined)) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter a price.", type: "error" });
            return false;
        }
        if ((operation === "delete" || operation === "update") && !addOnDetails["id"]) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "No add-on selected for deletion.", type: "error" });
            return false;
        }
        let payload: any = {};
        if (operation === "create") {
            payload = {
                product_type: 'addon',
                domain: domainId,
                features: addOnDetails.features || [],
                ...addOnDetails,
                pricing_type: 'fixed', // AddOns always use fixed pricing
                price: addOnDetails.price === '' ? null : addOnDetails.price,
                external_url: null, // AddOns don't support external links
                // Explicitly include validity (defaults to 'month' in form, but ensure it's sent)
                validity: addOnDetails.validity || 'month',
                ...(addOnDetails.validity === 'custom' && addOnDetails.validity_time ? { validity_time: addOnDetails.validity_time } : {}),
                parent_plans: addOnDetails.parent_plans || []
            };
        } else if (operation === "update") {
            const { categories: _categories, partners: _partners, parent_plans: _parent_plans, ...addOnWithoutFilters } = addOnDetails || {};
            payload = {
                product_type: 'addon',
                product_id: addOnDetails.id,
                ...addOnWithoutFilters,
                pricing_type: 'fixed', // AddOns always use fixed pricing
                price: addOnDetails.price === '' ? null : addOnDetails.price,
                external_url: null, // AddOns don't support external links
                // Explicitly include validity to ensure it's sent
                validity: addOnDetails.validity || 'month',
                ...(addOnDetails.validity === 'custom' && addOnDetails.validity_time ? { validity_time: addOnDetails.validity_time } : {}),
                parent_plans: addOnDetails.parent_plans || []
            };
        } else if (operation === "delete") {
            payload = {
                product_type: 'addon',
                product_id: addOnDetails.id,
                todelete: true
            };
        }
        try {
            setshowloader(true);
            const response = await axios.post(
                "/payments/manageProduct/",
                payload,
                { withCredentials: true }
            );
            if (response && (response.status === "CREATED" || response.status === "VERSIONED" || response.status === "ARCHIVED" || response.status === "UPDATED" || response.status === "DELETED")) {
                let message = `Add-on ${operation}d successfully.`;
                if (response.status === "VERSIONED") {
                    message = "Your changes have been saved successfully.";
                } else if (response.status === "ARCHIVED") {
                    message = "Add-on archived successfully.";
                }
                setAlertMsgg({ ...alertMsgg, open: true, content: message, type: "success" });
                fetchPlans();
                setshowloader(false);
                return true;
            } else {
                const action = operation === "create" ? "create" : operation === "update" ? "update" : "delete";
                setAlertMsgg({ ...alertMsgg, open: true, content: `Unable to ${action} the add-on. Please try again.`, type: "error" });
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                const errorMsg = error.response.data?.error || "Unable to process the request. Please check all fields and try again.";
                setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to connect to the server. Please check your internet connection.", type: "error" });
            }
            setshowloader(false);
        }
        return false;
    }

    const handleSubscriptionAddOn = async (operation: string) => {
      if ((operation === "create" || operation === "update") && (!subscriptionAddOnDetails.title || !subscriptionAddOnDetails.title.trim())) {
        setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter subscription add-on title.", type: "error" });
        return false;
      }
      if ((operation === "create" || operation === "update") && (subscriptionAddOnDetails.price === '' || subscriptionAddOnDetails.price === null || subscriptionAddOnDetails.price === undefined)) {
        setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter a price.", type: "error" });
        return false;
      }
      if ((operation === "delete" || operation === "update") && !subscriptionAddOnDetails["id"]) {
        setAlertMsgg({ ...alertMsgg, open: true, content: "No subscription add-on selected for deletion.", type: "error" });
        return false;
      }
      let payload: any = {};
      if (operation === "create") {
        payload = {
          product_type: 'subscriptionaddon',
          domain: domainId,
          features: subscriptionAddOnDetails.features || [],
          ...subscriptionAddOnDetails,
          pricing_type: 'fixed', // Subscription AddOns always use fixed pricing
          price: subscriptionAddOnDetails.price === '' ? null : subscriptionAddOnDetails.price,
          external_url: null, // Subscription AddOns don't support external links
          // Explicitly include validity (defaults to 'month' in form, but ensure it's sent)
          validity: subscriptionAddOnDetails.validity || 'month',
          ...(subscriptionAddOnDetails.validity === 'custom' && subscriptionAddOnDetails.validity_time ? { validity_time: subscriptionAddOnDetails.validity_time } : {}),
          parent_plans: subscriptionAddOnDetails.parent_plans || []
        };
      } else if (operation === "update") {
        const { categories: _categories, partners: _partners, parent_plans: _parent_plans, ...subAddOnWithoutFilters } = subscriptionAddOnDetails || {};
        payload = {
          product_type: 'subscriptionaddon',
          product_id: subscriptionAddOnDetails.id,
          ...subAddOnWithoutFilters,
          pricing_type: 'fixed', // Subscription AddOns always use fixed pricing
          price: subscriptionAddOnDetails.price === '' ? null : subscriptionAddOnDetails.price,
          external_url: null, // Subscription AddOns don't support external links
          // Explicitly include validity to ensure it's sent
          validity: subscriptionAddOnDetails.validity || 'month',
          ...(subscriptionAddOnDetails.validity === 'custom' && subscriptionAddOnDetails.validity_time ? { validity_time: subscriptionAddOnDetails.validity_time } : {}),
          parent_plans: subscriptionAddOnDetails.parent_plans || []
        };
      } else if (operation === "delete") {
        payload = {
          product_type: 'subscriptionaddon',
          product_id: subscriptionAddOnDetails.id,
          todelete: true
        };
      }
      try {
        setshowloader(true);
        const response = await axios.post(
          "/payments/manageProduct/",
          payload,
          { withCredentials: true }
        );
        if (response && (response.status === "CREATED" || response.status === "VERSIONED" || response.status === "ARCHIVED" || response.status === "UPDATED" || response.status === "DELETED")) {
          let message = `Subscription Add-on ${operation}d successfully.`;
          if (response.status === "VERSIONED") {
              message = "Your changes have been saved successfully.";
          } else if (response.status === "ARCHIVED") {
              message = "Subscription Add-on archived successfully.";
          }
          setAlertMsgg({ ...alertMsgg, open: true, content: message, type: "success" });
          fetchPlans();
          setshowloader(false);
          return true;
        } else {
          const action = operation === "create" ? "create" : operation === "update" ? "update" : "delete";
          setAlertMsgg({ ...alertMsgg, open: true, content: `Unable to ${action} the subscription add-on. Please try again.`, type: "error" });
        }
        setshowloader(false);
      } catch (error: any) {
        if (error.response) {
          const errorMsg = error.response.data?.error || "Unable to process the request. Please check all fields and try again.";
          setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
        } else {
          setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to connect to the server. Please check your internet connection.", type: "error" });
        }
        setshowloader(false);
      }
      return false;
    }

    const handleOneTimeProduct = async (operation: string) => {
        if ((operation === "create" || operation === "update") && (!oneTimeProductDetails.title || !oneTimeProductDetails.title.trim())) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter one-time product title.", type: "error" });
            return false;
        }
        if ((operation === "create" || operation === "update") && (!oneTimeProductDetails.description || !oneTimeProductDetails.description.trim())) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter a description.", type: "error" });
            return false;
        }
        if ((operation === "create" || operation === "update") && oneTimeProductDetails.pricing_type === 'fixed' && (oneTimeProductDetails.price === '' || oneTimeProductDetails.price === null || oneTimeProductDetails.price === undefined)) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter a price for fixed pricing.", type: "error" });
            return false;
        }
        if ((operation === "create" || operation === "update") && oneTimeProductDetails.pricing_type === 'external' && (!oneTimeProductDetails.external_url || !oneTimeProductDetails.external_url.trim())) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please enter an external URL for buy externally.", type: "error" });
            return false;
        }
        if ((operation === "delete" || operation === "update") && !oneTimeProductDetails["id"]) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "No one-time product selected for deletion.", type: "error" });
            return false;
        }
        let payload: any = {};
        if (operation === "create") {
            // Handle price: null for contact, null for external if empty/0, otherwise use value
            let priceValue = null;
            if (oneTimeProductDetails.pricing_type === 'fixed') {
                priceValue = oneTimeProductDetails.price === '' || oneTimeProductDetails.price === null || oneTimeProductDetails.price === undefined ? null : oneTimeProductDetails.price;
            } else if (oneTimeProductDetails.pricing_type === 'external') {
                // For external, price is optional - set to null if empty, 0, or not provided
                priceValue = (oneTimeProductDetails.price === '' || oneTimeProductDetails.price === null || oneTimeProductDetails.price === undefined || oneTimeProductDetails.price === 0) ? null : oneTimeProductDetails.price;
            }
            
            payload = {
                product_type: 'onetimeproduct',
                domain: domainId,
                features: oneTimeProductDetails.features || [],
                ...oneTimeProductDetails,
                pricing_type: oneTimeProductDetails.pricing_type || 'fixed',
                price: priceValue,
                external_url: oneTimeProductDetails.pricing_type === 'external' ? (oneTimeProductDetails.external_url || '') : null,
                // One-time products should have empty validity (or not set)
                validity: oneTimeProductDetails.validity !== undefined ? oneTimeProductDetails.validity : ''
            };
        } else if (operation === "update") {
            const { categories: _categories, partners: _partners, ...oneTimeWithoutCategories } = oneTimeProductDetails || {};
            
            // Handle price: null for contact, null for external if empty/0, otherwise use value
            let priceValue = null;
            if (oneTimeProductDetails.pricing_type === 'fixed') {
                priceValue = oneTimeProductDetails.price === '' || oneTimeProductDetails.price === null || oneTimeProductDetails.price === undefined ? null : oneTimeProductDetails.price;
            } else if (oneTimeProductDetails.pricing_type === 'external') {
                // For external, price is optional - set to null if empty, 0, or not provided
                priceValue = (oneTimeProductDetails.price === '' || oneTimeProductDetails.price === null || oneTimeProductDetails.price === undefined || oneTimeProductDetails.price === 0) ? null : oneTimeProductDetails.price;
            }
            
            payload = {
                product_type: 'onetimeproduct',
                product_id: oneTimeProductDetails.id,
                ...oneTimeWithoutCategories,
                pricing_type: oneTimeProductDetails.pricing_type || 'fixed',
                price: priceValue,
                external_url: oneTimeProductDetails.pricing_type === 'external' ? (oneTimeProductDetails.external_url || '') : null,
                // Explicitly include validity (should be empty for one-time products)
                validity: oneTimeProductDetails.validity !== undefined ? oneTimeProductDetails.validity : ''
            };
        } else if (operation === "delete") {
            payload = {
                product_type: 'onetimeproduct',
                product_id: oneTimeProductDetails.id,
                todelete: true
            };
        }
        try {
            setshowloader(true);
            const response = await axios.post(
                "/payments/manageProduct/",
                payload,
                { withCredentials: true }
            );
            if (response && (response.status === "CREATED" || response.status === "VERSIONED" || response.status === "ARCHIVED" || response.status === "UPDATED" || response.status === "DELETED")) {
                let message = `One-Time Product ${operation}d successfully.`;
                if (response.status === "VERSIONED") {
                    message = "Your changes have been saved successfully.";
                } else if (response.status === "ARCHIVED") {
                    message = "One-Time Product archived successfully.";
                }
                setAlertMsgg({ ...alertMsgg, open: true, content: message, type: "success" });
                fetchPlans();
                setshowloader(false);
                return true;
            } else {
                const action = operation === "create" ? "create" : operation === "update" ? "update" : "delete";
                setAlertMsgg({ ...alertMsgg, open: true, content: `Unable to ${action} the one-time product. Please try again.`, type: "error" });
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                const errorMsg = error.response.data?.error || "Unable to process the request. Please check all fields and try again.";
                setAlertMsgg({ ...alertMsgg, open: true, content: errorMsg, type: "error" });
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Unable to connect to the server. Please check your internet connection.", type: "error" });
            }
            setshowloader(false);
        }
        return false;
    }

    const checkStripeMode = async (item: any, itemId: string, itemType: string) => {
        const key = `${itemType}_${itemId}`;
        setCheckingStripe({ ...checkingStripe, [key]: true });
        
        try {
            // Combine results
            let combinedResult: any = {
                product_check: null,
                price_check: null,
                overall_mode: 'UNKNOWN'
            };
            
            // Check product if exists
            if (item.strp_prod_id) {
                const prodResponse = await axios.post("/payments/stripe_mode_check/", {
                    stripe_id: item.strp_prod_id,
                    object_type: 'product'
                }, { withCredentials: true });
                
                if (prodResponse && prodResponse.status === "CHECKED") {
                    combinedResult.product_check = prodResponse.results;
                }
            }
            
            // Check price if exists
            if (item.strp_price_id) {
                const priceResponse = await axios.post("/payments/stripe_mode_check/", {
                    stripe_id: item.strp_price_id,
                    object_type: 'price'
                }, { withCredentials: true });
                
                if (priceResponse && priceResponse.status === "CHECKED") {
                    combinedResult.price_check = priceResponse.results;
                }
            }
            
            // Determine overall mode
            const prodTest = combinedResult.product_check?.exists_in_test;
            const prodLive = combinedResult.product_check?.exists_in_live;
            const priceTest = combinedResult.price_check?.exists_in_test;
            const priceLive = combinedResult.price_check?.exists_in_live;
            
            if ((prodTest || !item.strp_prod_id) && (priceTest || !item.strp_price_id)) {
                combinedResult.overall_mode = 'TEST MODE ONLY';
            } else if ((prodLive || !item.strp_prod_id) && (priceLive || !item.strp_price_id)) {
                combinedResult.overall_mode = 'LIVE MODE ONLY';
            } else if ((prodTest && prodLive) || (priceTest && priceLive)) {
                combinedResult.overall_mode = 'EXISTS IN BOTH';
            } else {
                combinedResult.overall_mode = 'MIXED MODES (NEEDS SYNC)';
            }
            
            setStripeCheckResults({ 
                ...stripeCheckResults, 
                [key]: combinedResult 
            });
        } catch (error: any) {
            console.error(error);
            if (error.response) {
                setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error checking Stripe mode", type: "error" });
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
        } finally {
            setCheckingStripe({ ...checkingStripe, [key]: false });
        }
    }

    const moveToLive = async (itemId: string, itemType: string) => {
        try {
            setshowloader(true);
            
            // Map itemType to product_type
            const productTypeMap: Record<string, string> = {
                'plan': 'plan',
                'addon': 'addon',
                'subaddon': 'subscriptionaddon',
                'optional': 'planoptionals'
            };
            
            const response = await axios.post(
                "/payments/move_product_price_to_live/",
                {
                    product_type: productTypeMap[itemType],
                    product_id: itemId
                },
                { withCredentials: true }
            );
            
            if (response && response.status === "MOVED_TO_LIVE") {
                let message = response.message;
                if (response.results.product) {
                    message += `\nProduct: ${response.results.product.old_id} → ${response.results.product.new_id}`;
                }
                if (response.results.price) {
                    message += `\nPrice: ${response.results.price.old_id} → ${response.results.price.new_id}`;
                }
                
                setAlertMsgg({ 
                    ...alertMsgg, 
                    open: true, 
                    content: message, 
                    type: "success" 
                });
                // Refresh the data
                fetchPlans();
                // Clear the check results for this item
                const key = `${itemType}_${itemId}`;
                const updatedResults = { ...stripeCheckResults };
                delete updatedResults[key];
                setStripeCheckResults(updatedResults);
            }
            setshowloader(false);
        } catch (error: any) {
            console.error(error);
            if (error.response) {
                setAlertMsgg({ 
                    ...alertMsgg, 
                    open: true, 
                    content: error.response.data?.error || "Error moving to Live mode", 
                    type: "error" 
                });
            } else {
                setAlertMsgg({ 
                    ...alertMsgg, 
                    open: true, 
                    content: "Network error", 
                    type: "error" 
                });
            }
            setshowloader(false);
        }
    }

    const moveToTest = async (itemId: string, itemType: string) => {
        try {
            setshowloader(true);
            
            // Map itemType to product_type
            const productTypeMap: Record<string, string> = {
                'plan': 'plan',
                'addon': 'addon',
                'subaddon': 'subscriptionaddon',
                'optional': 'planoptionals'
            };
            
            const response = await axios.post(
                "/payments/move_product_price_to_test/",
                {
                    product_type: productTypeMap[itemType],
                    product_id: itemId
                },
                { withCredentials: true }
            );
            
            if (response && response.status === "MOVED_TO_TEST") {
                let message = response.message;
                if (response.results.product) {
                    message += `\nProduct: ${response.results.product.old_id} → ${response.results.product.new_id}`;
                }
                if (response.results.price) {
                    message += `\nPrice: ${response.results.price.old_id} → ${response.results.price.new_id}`;
                }
                
                setAlertMsgg({ 
                    ...alertMsgg, 
                    open: true, 
                    content: message, 
                    type: "success" 
                });
                // Refresh the data
                fetchPlans();
                // Clear the check results for this item
                const key = `${itemType}_${itemId}`;
                const updatedResults = { ...stripeCheckResults };
                delete updatedResults[key];
                setStripeCheckResults(updatedResults);
            }
            setshowloader(false);
        } catch (error: any) {
            console.error(error);
            if (error.response) {
                setAlertMsgg({ 
                    ...alertMsgg, 
                    open: true, 
                    content: error.response.data?.error || "Error moving to Test mode", 
                    type: "error" 
                });
            } else {
                setAlertMsgg({ 
                    ...alertMsgg, 
                    open: true, 
                    content: "Network error", 
                    type: "error" 
                });
            }
            setshowloader(false);
        }
    }

    const renderStripeCheckResult = (key: string, itemId: string, itemType: string) => {
        const result = stripeCheckResults[key];
        if (!result) return null;

        return (
            <div style={{
                marginTop: 8,
                padding: 12,
                background: '#f5f5f5',
                borderRadius: 8,
                fontSize: 12,
                border: '1px solid #e0e0e0'
            }}>
                <div style={{fontWeight: 600, marginBottom: 6, color: '#222'}}>
                    {result.overall_mode}
                </div>
                
                {result.product_check && (
                    <div style={{fontSize: 11, color: '#666', marginBottom: 2}}>
                        Product: {result.product_check.exists_in_test && <span style={{color: '#f57c00'}}>TEST</span>}
                        {result.product_check.exists_in_test && result.product_check.exists_in_live && <span> + </span>}
                        {result.product_check.exists_in_live && <span style={{color: '#388e3c'}}>LIVE</span>}
                    </div>
                )}
                
                {result.price_check && (
                    <div style={{fontSize: 11, color: '#666', marginBottom: 4}}>
                        Price: {result.price_check.exists_in_test && <span style={{color: '#f57c00'}}>TEST</span>}
                        {result.price_check.exists_in_test && result.price_check.exists_in_live && <span> + </span>}
                        {result.price_check.exists_in_live && <span style={{color: '#388e3c'}}>LIVE</span>}
                    </div>
                )}
                
                {/* Show Move to Live if in TEST MODE ONLY */}
                {result.overall_mode === 'TEST MODE ONLY' && (
                    <button
                        className="btn btn-sm btn-warning mt-2"
                        onClick={() => moveToLive(itemId, itemType)}
                        style={{fontSize: 11, padding: '4px 12px'}}
                    >
                        <i className="bx bx-transfer" style={{marginRight: 4}}></i>
                        Move to Live
                    </button>
                )}
                
                {/* Show Move to Test if in LIVE MODE ONLY */}
                {result.overall_mode === 'LIVE MODE ONLY' && (
                    <button
                        className="btn btn-sm btn-info mt-2"
                        onClick={() => moveToTest(itemId, itemType)}
                        style={{fontSize: 11, padding: '4px 12px'}}
                    >
                        <i className="bx bx-transfer" style={{marginRight: 4}}></i>
                        Move to Test
                    </button>
                )}
            </div>
        );
    };

    // Sync domain name from navigation state when available
    useEffect(() => {
        if (domainNameFromState) setDomainName(domainNameFromState);
    }, [domainNameFromState]);

    // Fetch domain name when opening page directly (no state) or after refresh
    useEffect(() => {
        if (domainId && !domainNameFromState) fetchDomainName();
    }, [domainId, domainNameFromState]);

    useEffect(() => {
        fetchCategories();
        fetchPartners();
        fetchPlans();
    }, []);

    // Reset navigation indices when plans or plan optionals change
    useEffect(() => {
        // Reset plans index if current index is out of bounds
        if (plansStartIndex >= plans.length && plans.length > 0) {
            setPlansStartIndex(Math.max(0, plans.length - PLANS_VISIBLE_COUNT));
        } else if (plans.length <= PLANS_VISIBLE_COUNT) {
            setPlansStartIndex(0);
        }
    }, [plans.length]);

    useEffect(() => {
        // Reset plan optionals index if current index is out of bounds
        if (planOptionalsStartIndex >= planOptionalGroups.length && planOptionalGroups.length > 0) {
            setPlanOptionalsStartIndex(Math.max(0, planOptionalGroups.length - PLAN_OPTIONALS_VISIBLE_COUNT));
        } else if (planOptionalGroups.length <= PLAN_OPTIONALS_VISIBLE_COUNT) {
            setPlanOptionalsStartIndex(0);
        }
    }, [planOptionalGroups.length]);

    function removeBodyCss() {
        document.body.classList.add("no_padding");
    }

    function toggleDeletePlanModal() {
        setDeletePlanModal(!deletePlanModal);
        removeBodyCss();
    }

    const toggleAddUpdatePlanModal = () => {
        setAddPlanModal(!addPlanModal);
        removeBodyCss();
    }

    document.title = domainName ? `${domainName} - Packages | BizServe` : "Domain Packages | BizServe";

    return <>

        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumbs title="Admin" breadcrumbItem={domainName ? `Packages - ${domainName}` : "Packages"} />

                <div className="d-flex justify-content-end mb-3">
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>Category</span>
                        <select
                            className="form-control"
                            style={{ minWidth: 220 }}
                            value={selectedCategoryFilter}
                            onChange={(e) => {
                                const next = e.target.value;
                                setSelectedCategoryFilter(next);
                                fetchPlans(next, undefined);
                            }}
                        >
                            <option value="all">All</option>
                            {categories.map((cat: any) => (
                                <option key={cat.id} value={String(cat.id)}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                            Partner{" "}
                            <Info
                                id="partner-filter-note"
                                size={16}
                                style={{ cursor: "pointer", display: "inline-block", verticalAlign: "middle" }}
                            />
                        </span>
                        <UncontrolledTooltip placement="top" target="partner-filter-note">
                            {partnerPickNote}
                        </UncontrolledTooltip>
                        <select
                            className="form-control"
                            style={{ minWidth: 220 }}
                            value={selectedPartnerFilter}
                            onChange={(e) => {
                                const next = e.target.value;
                                setSelectedPartnerFilter(next);
                                fetchPlans(undefined, next);
                            }}
                        >
                            <option value="all">All</option>
                            <option value="unassigned">Unassigned</option>
                            {partners.map((p: any) => (
                                <option key={p.id} value={String(p.id)}>
                                    {p.name || `Partner #${p.id}`}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                {alertMsgg.open && (
                    <Alert
                        color={alertMsgg.type === "success" ? "success" : "danger"}
                        toggle={handleAlertMsggClose}
                        fade={false}
                        style={{ fontWeight: "bold" }}
                    >
                        {alertMsgg.content}
                    </Alert>
                )}
                <div className="loaderContainer" hidden={!showloader}>
                    <div className="spinner">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                </div>
                <Row>
                    <Col md={12}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: 16}}>
                                <h4 className="mb-0" style={{fontWeight: 700, color: '#222'}}>Products</h4>
                            </div>
                            <div style={{display: 'flex', gap: 10}}>
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    type="button"
                                    onClick={() => { setCategoryDetails({}); setManageCategoriesModal(true); }}
                                >
                                    Manage Categories
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    type="button"
                                    onClick={() => { setManagePartnersModal(true); }}
                                >
                                    Manage Partners
                                </button>
                            </div>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
                            <h5 className="mb-0" style={{fontWeight: 600, color: '#444'}}>Plans</h5>
                            {plans.length > PLANS_VISIBLE_COUNT && (
                                <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={navigatePlansLeft}
                                        disabled={plansStartIndex === 0}
                                        style={{minWidth: 40, padding: '6px 12px'}}
                                    >
                                        <i className="bx bx-chevron-left" style={{fontSize: 20}}></i>
                                    </button>
                                    <span style={{fontSize: 14, color: '#666', minWidth: 80, textAlign: 'center'}}>
                                        {plansStartIndex + 1}-{Math.min(plansStartIndex + PLANS_VISIBLE_COUNT, plans.length)} of {plans.length}
                                    </span>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={navigatePlansRight}
                                        disabled={plansStartIndex >= plans.length - PLANS_VISIBLE_COUNT}
                                        style={{minWidth: 40, padding: '6px 12px'}}
                                    >
                                        <i className="bx bx-chevron-right" style={{fontSize: 20}}></i>
                                    </button>
                                </div>
                            )}
                        </div>
                        <div style={{width: '100%', overflowX: 'auto', marginTop: 24}}>
                            <table style={{width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)'}}>
                                <thead>
                                    <tr>
                                        <th style={{width: 180, border: '1px solid #e0e0e0', background: 'transparent'}}></th>
                                        {getVisiblePlans().map((plan: any, visibleIdx: number) => {
                                            const planIdx = plansStartIndex + visibleIdx;
                                            return (
                                            <th key={plan.id} style={{width: 'calc((100% - 180px) / 3)', padding: 24, border: '1px solid #e0e0e0', textAlign: 'center', background: 'transparent', position: 'relative'}}>
                                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', width: '100%', position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, paddingLeft: 8, paddingRight: 8}}>
                                                        <div style={{display: 'flex', gap: 4}}>
                                                            <button 
                                                                className="btn btn-link p-0" 
                                                                style={{color: planIdx === 0 ? '#ccc' : '#1976d2'}} 
                                                                onClick={() => planIdx !== 0 && movePriority('plan', plan.id, 'up')}
                                                                disabled={planIdx === 0}
                                                                title="Move up"
                                                            >
                                                                <i className="bx bx-chevron-up" style={{fontSize: 22}}></i>
                                                            </button>
                                                            <button 
                                                                className="btn btn-link p-0" 
                                                                style={{color: planIdx === plans.length - 1 ? '#ccc' : '#1976d2'}} 
                                                                onClick={() => planIdx !== plans.length - 1 && movePriority('plan', plan.id, 'down')}
                                                                disabled={planIdx === plans.length - 1}
                                                                title="Move down"
                                                            >
                                                                <i className="bx bx-chevron-down" style={{fontSize: 22}}></i>
                                                            </button>
                                                        </div>
                                                        <UncontrolledDropdown direction="down">
                                                            <DropdownToggle tag="button" className="btn btn-link p-0" style={{color: '#888'}}>
                                                                <i className="bx bx-dots-vertical-rounded" style={{fontSize: 22}}></i>
                                                            </DropdownToggle>
                                                            <DropdownMenu end style={{minWidth: 120}}>
                                                                    <DropdownItem onClick={() => { 
                                                                        setPlanDetails({ ...plan, pricing_type: plan.pricing_type || 'fixed', categories: extractCategoryIds(plan), partners: extractPartnersWithShare(plan) }); 
                                                                        setFeaturesInput(""); 
                                                                        toggleAddUpdatePlanModal(); 
                                                                    }}>
                                                                    <i className="bx bx-edit-alt me-2"></i>Edit
                                                                </DropdownItem>
                                                                <DropdownItem onClick={() => openProductFiltersModal({ ...plan, categories: extractCategoryIds(plan) }, "plan")}>
                                                                    <i className="bx bx-filter-alt me-2"></i>Filters
                                                                </DropdownItem>
                                                                <DropdownItem divider />
                                                                <DropdownItem className="text-danger" onClick={() => { setPlanDetails(plan); toggleDeletePlanModal(); }}>
                                                                    <i className="bx bx-trash me-2"></i>Delete
                                                                </DropdownItem>
                                                            </DropdownMenu>
                                                        </UncontrolledDropdown>
                                                    </div>
                                                    <div style={{fontWeight: 700, fontSize: 20, color: '#222', marginBottom: 4, marginTop: 8}}>{plan.title}</div>
                                                </div>
                                            </th>
                                            );
                                        })}
                                        {/* Add New Plan column */}
                                        <th key="add-new-plan" style={{padding: 24, border: '1px dashed #1976d2', textAlign: 'center', background: 'transparent', cursor: 'pointer'}} onClick={() => { 
                                            const defId = getDefaultCategoryId();
                                            setPlanDetails({ pricing_type: 'fixed', categories: defId ? [defId] : [], partners: [], features: [] }); 
                                            setFeaturesInput(""); 
                                            toggleAddUpdatePlanModal(); 
                                        }}>
                                            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#1976d2'}}>
                                                <i className="bx bx-plus-circle" style={{fontSize: 32, marginBottom: 6}}></i>
                                                <span style={{fontWeight: 600}}>Add New Plan</span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Description Row */}
                                    <tr>
                                        <td style={{fontWeight: 600, fontSize: 15, color: '#222', background: '#fafbfc', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle'}}>Description</td>
                                        {getVisiblePlans().map((plan: any) => (
                                            <td key={plan.id + '-desc'} style={{padding: 16, background: '#fafbfc', border: '1px solid #e0e0e0', fontSize: 13, color: '#555'}}>
                                                <ReactMarkdown
                                                    components={{
                                                        p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 13, color: '#555' }}>{children}</p>,
                                                        strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                                        em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                        ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ul>,
                                                        ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ol>,
                                                        li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 13, color: '#555' }}>{children}</li>,
                                                        a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                                    }}
                                                >
                                                    {cleanMarkdown(plan.description)}
                                                </ReactMarkdown>
                                            </td>
                                        ))}
                                        <td style={{background: '#fafbfc', border: '1px dashed #1976d2'}}></td>
                                    </tr>
                                    {/* Price Row */}
                                    <tr>
                                        <td style={{fontWeight: 600, fontSize: 15, color: '#222', background: '#fff', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle'}}>Price</td>
                                        {getVisiblePlans().map((plan: any) => (
                                            <td key={plan.id + '-price'} style={{padding: 16, background: '#fff', border: '1px solid #e0e0e0', fontWeight: 700, fontSize: 20, color: '#1976d2'}}>
                                                {plan.price !== null && plan.price !== undefined ? (
                                                    <>
                                                        ${formatPrice(plan.price)}
                                                        {plan.validity && <span style={{fontWeight: 400, fontSize: 13, color: '#888'}}> / {plan.validity}</span>}
                                                    </>
                                                ) : (
                                                    <span style={{fontSize: 16, fontWeight: 500, color: '#666'}}>Contact for pricing</span>
                                                )}
                                            </td>
                                        ))}
                                        <td style={{background: '#fff', border: '1px dashed #1976d2'}}></td>
                                    </tr>
                                    {/* Features Row */}
                                    <tr>
                                        <td style={{fontWeight: 600, fontSize: 15, color: '#222', background: '#fafbfc', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle'}}>Features</td>
                                        {getVisiblePlans().map((plan: any) => (
                                            <td key={plan.id + '-features'} style={{padding: 16, verticalAlign: 'top', background: '#fafbfc', border: '1px solid #e0e0e0'}}>
                                                <ul style={{listStyle: 'disc', paddingLeft: 18, margin: 0, fontSize: 13, color: '#444'}}>
                                                    {plan.features && plan.features.map((f: string, i: number) => <li key={i}>{f}</li>)}
                                                </ul>
                                            </td>
                                        ))}
                                        <td style={{background: '#fafbfc', border: '1px dashed #1976d2'}}></td>
                                    </tr>
                                    {/* Filters Row */}
                                    <tr>
                                        <td style={{fontWeight: 600, fontSize: 15, color: '#222', background: '#fff', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle'}}>Filters</td>
                                        {getVisiblePlans().map((plan: any) => (
                                            <td key={plan.id + '-filters'} style={{padding: 16, verticalAlign: 'top', background: '#fff', border: '1px solid #e0e0e0'}}>
                                                <FiltersChips item={plan} />
                                            </td>
                                        ))}
                                        <td style={{background: '#fff', border: '1px dashed #1976d2'}}></td>
                                    </tr>
                                    {/* Stripe Check Row */}
                                    <tr>
                                        <td style={{fontWeight: 600, fontSize: 15, color: '#222', background: '#fff', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle'}}>Stripe Status</td>
                                        {getVisiblePlans().map((plan: any) => (
                                            <td key={plan.id + '-stripe'} style={{padding: 16, verticalAlign: 'top', background: '#fff', border: '1px solid #e0e0e0'}}>
                                                {(plan.strp_prod_id || plan.strp_price_id) ? (
                                                    <div>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => checkStripeMode(plan, plan.id, 'plan')}
                                                            disabled={checkingStripe[`plan_${plan.id}`]}
                                                            style={{fontSize: 11, padding: '4px 12px', marginBottom: 8}}
                                                        >
                                                            {checkingStripe[`plan_${plan.id}`] ? 'Checking...' : 'Check Stripe'}
                                                        </button>
                                                        {renderStripeCheckResult(`plan_${plan.id}`, plan.id, 'plan')}
                                                    </div>
                                                ) : (
                                                    <div style={{fontSize: 12, color: '#999'}}>No Stripe IDs</div>
                                                )}
                                            </td>
                                        ))}
                                        <td style={{background: '#fff', border: '1px dashed #1976d2'}}></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </Col>
                </Row>

                {/* Add/Update Plan */}
                <Modal
                    isOpen={addPlanModal}
                    size="lg"
                    toggle={() => {setFeaturesInput(""); toggleAddUpdatePlanModal()}}
                    backdrop={'static'}
                    centered
                >
                    <ModalHeader toggle={() => {setFeaturesInput(""); toggleAddUpdatePlanModal()}}>
                        <div className="modal-title mt-0 font-size-16 text-dark">
                            {planDetails && planDetails.id ? "Edit Plan" : "Add Plan"}
                        </div>
                    </ModalHeader>
                    <div className="modal-body">
                        <form>
                            <Row>
                                <Col lg={6}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Plan Title <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="plan-title-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="plan-title-info">
                                            Enter a descriptive name for this subscription plan
                                        </UncontrolledTooltip>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={planDetails.title}
                                            onChange={e => setPlanDetails({ ...planDetails, title: e.target.value })}
                                            placeholder="Enter plan title"
                                        />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Pricing Type <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="plan-pricing-type-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="plan-pricing-type-info">
                                            Choose how this plan is priced: Fixed Price, Contact for Pricing, or Buy Externally
                                        </UncontrolledTooltip>
                                        <select
                                            className="form-control"
                                            value={planDetails.pricing_type || 'fixed'}
                                            onChange={e => {
                                                const pricingType = e.target.value;
                                                const updates: any = { pricing_type: pricingType };
                                                if (pricingType === 'contact') {
                                                    updates.price = null;
                                                    updates.external_url = null;
                                                } else if (pricingType === 'external') {
                                                    updates.external_url = planDetails.external_url || '';
                                                } else {
                                                    updates.external_url = null;
                                                }
                                                setPlanDetails({ ...planDetails, ...updates });
                                            }}
                                        >
                                            <option value="fixed">Fixed Price</option>
                                            <option value="contact">Contact for Pricing</option>
                                            <option value="external">Buy Externally</option>
                                        </select>
                                    </div>
                                </Col>
                                {planDetails.pricing_type === 'external' && (
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                External URL <span className="text-danger">*</span>{" "}
                                                <Info 
                                                    id="plan-external-url-info" 
                                                    size={16} 
                                                    style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                                />
                                            </label>
                                            <UncontrolledTooltip placement="top" target="plan-external-url-info">
                                                URL where customers will be redirected to purchase this plan externally
                                            </UncontrolledTooltip>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={planDetails.external_url || ''}
                                                onChange={e => setPlanDetails({ ...planDetails, external_url: e.target.value })}
                                                placeholder="https://example.com/purchase"
                                                required
                                            />
                                        </div>
                                    </Col>
                                )}
                                <Col lg={6}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Price (USD){" "}
                                            {planDetails.pricing_type === 'fixed' && <span className="text-danger">*</span>}
                                            <Info 
                                                id="plan-price-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="plan-price-info">
                                            {planDetails.pricing_type === 'fixed' && 'Set the recurring price in USD for this plan.'}
                                            {planDetails.pricing_type === 'external' && 'Set the price displayed to customers (optional for external links).'}
                                            {planDetails.pricing_type === 'contact' && 'Not applicable for "Contact for Pricing" option.'}
                                        </UncontrolledTooltip>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={planDetails.price ?? ''}
                                            onChange={e => {
                                                const value = e.target.value;
                                                // For external pricing, treat '0' as empty (optional price)
                                                if (planDetails.pricing_type === 'external' && value === '0') {
                                                    setPlanDetails({ ...planDetails, price: '' });
                                                } else {
                                                    setPlanDetails({ ...planDetails, price: value });
                                                }
                                            }}
                                            placeholder={planDetails.pricing_type === 'contact' ? 'Not applicable' : 'Enter price'}
                                            min="0"
                                            step="0.01"
                                            disabled={planDetails.pricing_type === 'contact'}
                                            required={planDetails.pricing_type === 'fixed'}
                                        />
                                    </div>
                                </Col>
                                <Col lg={6}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Subscription Period{" "}
                                            <Info 
                                                id="plan-period-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="plan-period-info">
                                            Define how often this plan renews. Leave empty for one-time purchase (Once).
                                        </UncontrolledTooltip>
                                        <select
                                            className="form-control"
                                            value={planDetails.validity || ''}
                                            onChange={e => setPlanDetails({ ...planDetails, validity: e.target.value === '' ? '' : e.target.value })}
                                        >
                                            <option value="">Once</option>
                                            <option value="day">Day</option>
                                            <option value="week">Week</option>
                                            <option value="month">Month</option>
                                            <option value="quarter">Quarter</option>
                                            <option value="year">Year</option>
                                            <option value="lifetime">Lifetime</option>
                                            <option value="custom">Custom</option>
                                        </select>
                                        {planDetails.validity === 'custom' && (
                                            <input
                                                type="number"
                                                className="form-control mt-2"
                                                placeholder="Enter number of days *"
                                                min={1}
                                                value={planDetails.validity_time || ''}
                                                onChange={e => setPlanDetails({ ...planDetails, validity_time: parseInt(e.target.value) })}
                                            />
                                        )}
                                    </div>
                                </Col>
                                <Col lg={6}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Cancellation Days{" "}
                                            <Info 
                                                id="plan-cancellation-days-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="plan-cancellation-days-info">
                                            Number of days after purchase when this product can be cancelled or refunded
                                        </UncontrolledTooltip>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={planDetails.cancellation_days ?? 5}
                                            onChange={e => {
                                                const val = parseInt(e.target.value);
                                                setPlanDetails({ ...planDetails, cancellation_days: isNaN(val) ? 5 : val });
                                            }}
                                            placeholder="5"
                                            min="0"
                                        />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Description <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="plan-description-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="plan-description-info">
                                            Brief overview of what this plan includes
                                        </UncontrolledTooltip>
                                        <textarea
                                            className="form-control"
                                            value={planDetails.description}
                                            onChange={e => setPlanDetails({ ...planDetails, description: e.target.value })}
                                            placeholder="Enter description"
                                        />
                                    </div>
                                </Col>
                                {planDetails && planDetails.id ? (
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <label className="form-label">Filters</label>
                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => { setFeaturesInput(""); toggleAddUpdatePlanModal(); openProductFiltersModal(planDetails, "plan"); }}
                                                >
                                                    Filters
                                                </button>
                                            </div>
                                        </div>
                                    </Col>
                                ) : (
                                    <>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Categories <span className="text-danger">*</span>
                                                </label>
                                                <CategoryMultiSelect
                                                    selectedIds={planDetails.categories || []}
                                                    maxVisibleItems={4}
                                                    onChange={(ids) => setPlanDetails({ ...planDetails, categories: ids })}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <label className="form-label">Partners</label>
                                                <PartnerMultiSelect
                                                    selectedPartners={planDetails.partners || []}
                                                    maxVisibleItems={4}
                                                    onChange={(partners) => setPlanDetails({ ...planDetails, partners })}
                                                />
                                                <small className="form-text text-muted">
                                                    Optional. If none are selected, this product is not assigned to any partner. {partnerPickNote}
                                                </small>
                                            </div>
                                        </Col>
                                    </>
                                )}
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Features <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="plan-features-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="plan-features-info">
                                            List the key features included. Enter a feature and press Enter or click Add.
                                        </UncontrolledTooltip>
                                        <InputGroup>
                                            <Input
                                                type="text"
                                                value={featuresInput}
                                                onChange={(e) => setFeaturesInput(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        if (featuresInput.trim()) {
                                                            const newFeatures = [...(planDetails.features || []), featuresInput.trim()];
                                                            setPlanDetails({ ...planDetails, features: newFeatures });
                                                            setFeaturesInput("");
                                                        }
                                                    }
                                                }}
                                                placeholder="Enter a feature and press Enter"
                                            />
                                            <Button color="primary" onClick={() => {
                                                if (featuresInput.trim()) {
                                                    const newFeatures = [...(planDetails.features || []), featuresInput.trim()];
                                                    setPlanDetails({ ...planDetails, features: newFeatures });
                                                    setFeaturesInput("");
                                                }
                                            }}>
                                                Add
                                            </Button>
                                        </InputGroup>
                                        {planDetails.features && planDetails.features.length > 0 && (
                                            <div className="mt-2" style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                                                {planDetails.features.map((feature: string, index: number) => (
                                                    <Badge key={index} color="primary" className="d-inline-flex align-items-center" style={{ fontSize: "0.875rem", padding: "0.5rem", maxWidth: "100%", wordBreak: "break-word", whiteSpace: "normal", textAlign: "left", justifyContent: "flex-start" }}>
                                                        <span style={{ maxWidth: "calc(100% - 20px)", wordBreak: "break-word", textAlign: "left" }}>{feature}</span>
                                                        <X
                                                            size={14}
                                                            className="ms-2"
                                                            style={{ cursor: "pointer", flexShrink: 0 }}
                                                            onClick={() => {
                                                                const currentFeatures = planDetails.features || [];
                                                                const newFeatures = currentFeatures.filter((_: string, i: number) => i !== index);
                                                                setPlanDetails({ ...planDetails, features: newFeatures });
                                                            }}
                                                        />
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="d-flex justify-content-end">
                                        <button
                                            className="btn btn-primary mb-3"
                                            type="button"
                                            onClick={async () => {
                                                const ok = planDetails && planDetails.id
                                                    ? await handlePlan("update")
                                                    : await handlePlan("create");
                                                if (ok) {
                                                    setFeaturesInput("");
                                                    toggleAddUpdatePlanModal();
                                                }
                                            }}
                                        >
                                            {planDetails && planDetails.id ? "Update Plan" : "Add Plan"}
                                        </button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </Modal>

                {/* Delete Plan */}
                <Modal
                    isOpen={deletePlanModal}
                    toggle={() => {
                        toggleDeletePlanModal();
                    }}
                    centered
                    className="rounded-4"
                >
                    <ModalHeader className="py-3 px-4" toggle={() => { toggleDeletePlanModal(); }}>
                        <div className="modal-title mt-0 font-size-16 text-dark">
                            Delete Plan
                        </div>
                    </ModalHeader>

                    <div className="modal-body">
                        <p>Are you sure you want to delete this plan?</p>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-sm btn-soft-dark"
                            onClick={() => { toggleDeletePlanModal(); }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => {
                                handlePlan("delete");
                                toggleDeletePlanModal();
                            }}
                        >
                            Delete
                        </button>
                    </div>
                </Modal>

                <Row className="mt-5">
                    <Col md={12}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8}}>
                            <div>
                                <h4 className="mb-2" style={{fontWeight: 700, color: '#222'}}>Plan Optionals</h4>
                                <p className="text-muted mb-0" style={{fontSize: 14}}>
                                    Optional features that users can choose from within each group. For example: Speed options (Fast, Standard, Slow), Storage tiers (Basic, Premium), or Support levels (Email, Priority, Dedicated).
                                </p>
                            </div>
                            {planOptionalGroups.length > PLAN_OPTIONALS_VISIBLE_COUNT && (
                                <div style={{display: 'flex', gap: 8, alignItems: 'center', marginTop: 4}}>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={navigatePlanOptionalsLeft}
                                        disabled={planOptionalsStartIndex === 0}
                                        style={{minWidth: 40, padding: '6px 12px'}}
                                    >
                                        <i className="bx bx-chevron-left" style={{fontSize: 20}}></i>
                                    </button>
                                    <span style={{fontSize: 14, color: '#666', minWidth: 80, textAlign: 'center'}}>
                                        {planOptionalsStartIndex + 1}-{Math.min(planOptionalsStartIndex + PLAN_OPTIONALS_VISIBLE_COUNT, planOptionalGroups.length)} of {planOptionalGroups.length}
                                    </span>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={navigatePlanOptionalsRight}
                                        disabled={planOptionalsStartIndex >= planOptionalGroups.length - PLAN_OPTIONALS_VISIBLE_COUNT}
                                        style={{minWidth: 40, padding: '6px 12px'}}
                                    >
                                        <i className="bx bx-chevron-right" style={{fontSize: 20}}></i>
                                    </button>
                                </div>
                            )}
                        </div>
                        <div style={{width: '100%', overflowX: 'auto', marginTop: 24}}>
                            <table style={{width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)'}}>
                                <thead>
                                    <tr>
                                        <th style={{width: 180, border: '1px solid #e0e0e0', background: 'transparent'}}></th>
                                        {getVisiblePlanOptionalGroups().map(group => (
                                            <th key={group} style={{padding: 24, border: '1px solid #e0e0e0', textAlign: 'center', background: 'transparent', fontWeight: 500, fontSize: 15, color: '#444'}}>
                                                <div>{group}</div>
                                            </th>
                                        ))}
                                        <th key="add-new-optional" style={{padding: 24, border: '1px dashed #1976d2', textAlign: 'center', background: 'transparent', cursor: 'pointer'}} onClick={() => { const defId = getDefaultCategoryId(); setIsNewGroup(false); setPlanOptionalGroupInput(""); setPlanOptionalDetails({ pricing_type: 'fixed', categories: defId ? [defId] : [], partners: [], parent_plans: [], features: [] }); setPlanOptionalFeaturesInput(""); togglePlanOptionalModal(); }}>
                                            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#1976d2'}}>
                                                <i className="bx bx-plus-circle" style={{fontSize: 28, marginBottom: 6}}></i>
                                                <span style={{fontWeight: 600}}>Add Optional</span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Optionals Row */}
                                    <tr>
                                        <td style={{fontWeight: 600, fontSize: 15, color: '#222', background: '#fafbfc', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle'}}>Optionals</td>
                                        {getVisiblePlanOptionalGroups().map(group => (
                                            <td key={group + '-optionals'} style={{padding: 16, background: '#fafbfc', border: '1px solid #e0e0e0', verticalAlign: 'top'}}>
                                                <ul style={{listStyle: 'none', paddingLeft: 0, margin: 0}}>
                                                    {planOptionals.filter((o: any) => o.group === group).map((optional: any, idx: number, arr: any[]) => (
                                                        <>
                                                        <li key={optional.id} style={{marginBottom: 12, position: 'relative', paddingBottom: idx < arr.length - 1 ? 12 : 0, borderBottom: idx < arr.length - 1 ? '1px solid #e0e0e0' : 'none'}}>
                                                            <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                                                                <div style={{display: 'flex', flexDirection: 'column'}}>
                                                                    <button 
                                                                        className="btn btn-link p-0" 
                                                                        style={{color: idx === 0 ? '#ccc' : '#1976d2', fontSize: 12, lineHeight: 1}} 
                                                                        onClick={() => idx !== 0 && movePriority('planoptionals', optional.id, 'up')}
                                                                        disabled={idx === 0}
                                                                        title="Move up"
                                                                    >
                                                                        <i className="bx bx-chevron-up" style={{fontSize: 16}}></i>
                                                                    </button>
                                                                    <button 
                                                                        className="btn btn-link p-0" 
                                                                        style={{color: idx === arr.length - 1 ? '#ccc' : '#1976d2', fontSize: 12, lineHeight: 1}} 
                                                                        onClick={() => idx !== arr.length - 1 && movePriority('planoptionals', optional.id, 'down')}
                                                                        disabled={idx === arr.length - 1}
                                                                        title="Move down"
                                                                    >
                                                                        <i className="bx bx-chevron-down" style={{fontSize: 16}}></i>
                                                                    </button>
                                                                </div>
                                                                <div style={{flex: 1}}>
                                                                    <div style={{fontWeight: 600, color: '#222'}}>
                                                                        {optional.title}{' '}
                                                                        {optional.price !== null && optional.price !== undefined ? (
                                                                            <>
                                                                                <span style={{color: '#1976d2', fontWeight: 400}}>${formatPrice(optional.price)}</span>
                                                                                {optional.validity && optional.validity !== '' && optional.validity !== 'lifetime' && optional.validity !== 'custom' && (
                                                                                    <span style={{fontWeight: 400, fontSize: 13, color: '#888'}}> / {optional.validity}</span>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <span style={{color: '#666', fontWeight: 400, fontSize: 13}}>Contact for pricing</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div style={{fontSize: 13, color: '#555', marginBottom: 8, marginLeft: 32}}>
                                                                <ReactMarkdown
                                                                    components={{
                                                                        p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 13, color: '#555' }}>{children}</p>,
                                                                        strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                                                        em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                                        ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ul>,
                                                                        ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ol>,
                                                                        li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 13, color: '#555' }}>{children}</li>,
                                                                        a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                                                    }}
                                                                >
                                                                    {cleanMarkdown(optional.description)}
                                                                </ReactMarkdown>
                                                            </div>
                                                            
                                                            {/* Features Section */}
                                                            {optional.features && optional.features.length > 0 && (
                                                                <div style={{ marginLeft: 32, marginTop: 8, marginBottom: 8, paddingTop: 8, borderTop: '1px solid #e8e8e8' }}>
                                                                    <div style={{ fontSize: "0.75rem", color: "#6c757d", fontWeight: 600, marginBottom: 8 }}>
                                                                        Features
                                                                    </div>
                                                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                                                        {optional.features.map((feature: string, fIdx: number) => (
                                                                            <Badge
                                                                                key={fIdx}
                                                                                color="light"
                                                                                className="px-2 py-1"
                                                                                style={{
                                                                                    fontSize: "0.75rem",
                                                                                    color: "#495057",
                                                                                    border: "1px solid #dee2e6",
                                                                                    fontWeight: 400,
                                                                                    maxWidth: "100%",
                                                                                    wordBreak: "break-word",
                                                                                    whiteSpace: "normal",
                                                                                    textAlign: "left",
                                                                                }}
                                                                            >
                                                                                {feature}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            <div style={{ marginLeft: 32, marginBottom: 8 }}>
                                                                <FiltersChips item={optional} />
                                                            </div>
                                                            
                                                            {/* Stripe Check Section */}
                                                            {(optional.strp_prod_id || optional.strp_price_id) && (
                                                                <div style={{marginTop: 8, paddingTop: 8, borderTop: '1px solid #e8e8e8'}}>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => checkStripeMode(optional, optional.id, 'optional')}
                                                                        disabled={checkingStripe[`optional_${optional.id}`]}
                                                                        style={{fontSize: 10, padding: '2px 8px', marginBottom: 6}}
                                                                    >
                                                                        {checkingStripe[`optional_${optional.id}`] ? 'Checking...' : 'Check Stripe'}
                                                                    </button>
                                                                    {renderStripeCheckResult(`optional_${optional.id}`, optional.id, 'optional')}
                                                                </div>
                                                            )}
                                                            
                                                            <UncontrolledDropdown direction="down" style={{position: 'absolute', top: 0, right: 0}}>
                                                                <DropdownToggle tag="button" className="btn btn-link p-0" style={{color: '#888'}}>
                                                                    <i className="bx bx-dots-vertical-rounded" style={{fontSize: 20}}></i>
                                                                </DropdownToggle>
                                                                <DropdownMenu end style={{minWidth: 120}}>
                                                                    <DropdownItem onClick={() => { setPlanOptionalDetails({ ...optional, categories: extractCategoryIds(optional), parent_plans: extractParentPlanIds(optional), features: optional.features || [] }); setIsNewGroup(false); setPlanOptionalGroupInput(optional.group); setPlanOptionalFeaturesInput(""); togglePlanOptionalModal(); }}>
                                                                        <i className="bx bx-edit-alt me-2"></i>Edit
                                                                    </DropdownItem>
                                                                    <DropdownItem onClick={() => openProductFiltersModal({ ...optional, categories: extractCategoryIds(optional) }, "planoptionals")}>
                                                                        <i className="bx bx-filter-alt me-2"></i>Filters
                                                                    </DropdownItem>
                                                                    <DropdownItem divider />
                                                                    <DropdownItem className="text-danger" onClick={() => { setPlanOptionalDetails({ ...optional, categories: extractCategoryIds(optional), parent_plans: extractParentPlanIds(optional) }); toggleDeletePlanOptionalModal(); }}>
                                                                        <i className="bx bx-trash me-2"></i>Delete
                                                                    </DropdownItem>
                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        </li>
                                                        </>
                                                    ))}
                                                </ul>
                                            </td>
                                        ))}
                                        <td style={{background: '#fafbfc', border: '1px dashed #1976d2'}}></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </Col>
                </Row>

                {/* Add/Edit Plan Optional Modal */}
                <Modal isOpen={planOptionalModal} size="md" toggle={togglePlanOptionalModal} backdrop={'static'} centered>
                    <ModalHeader toggle={togglePlanOptionalModal}>
                        <div className="modal-title mt-0 font-size-16 text-dark">
                            {planOptionalDetails && planOptionalDetails.id ? "Edit Plan Optional" : "Add Plan Optional"}
                        </div>
                    </ModalHeader>
                    <div className="modal-body">
                        <form>
                            <Row>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Title <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="optional-title-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="optional-title-info">
                                            Name of this optional add-on for the plan
                                        </UncontrolledTooltip>
                                        <input type="text" className="form-control" value={planOptionalDetails.title || ''} onChange={e => setPlanOptionalDetails({ ...planOptionalDetails, title: e.target.value })} placeholder="Enter title" />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Price (USD){" "}
                                            <Info 
                                                id="optional-price-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="optional-price-info">
                                            Additional cost in USD for this optional feature. Leave empty for "Contact for pricing"
                                        </UncontrolledTooltip>
                                        <input type="number" className="form-control" value={planOptionalDetails.price ?? ''} onChange={e => setPlanOptionalDetails({ ...planOptionalDetails, price: e.target.value })} placeholder="Leave empty for contact pricing" min="0" step="0.01" />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Description <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="optional-description-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="optional-description-info">
                                            Explain what this optional feature provides
                                        </UncontrolledTooltip>
                                        <textarea className="form-control" value={planOptionalDetails.description || ''} onChange={e => setPlanOptionalDetails({ ...planOptionalDetails, description: e.target.value })} placeholder="Enter description" />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Features (Optional){" "}
                                            <Info 
                                                id="optional-features-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="optional-features-info">
                                            List the key features included. Enter a feature and press Enter or click Add.
                                        </UncontrolledTooltip>
                                        <InputGroup>
                                            <Input
                                                type="text"
                                                value={planOptionalFeaturesInput}
                                                onChange={(e) => setPlanOptionalFeaturesInput(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        if (planOptionalFeaturesInput.trim()) {
                                                            const newFeatures = [...(planOptionalDetails.features || []), planOptionalFeaturesInput.trim()];
                                                            setPlanOptionalDetails({ ...planOptionalDetails, features: newFeatures });
                                                            setPlanOptionalFeaturesInput("");
                                                        }
                                                    }
                                                }}
                                                placeholder="Enter a feature and press Enter"
                                            />
                                            <Button color="primary" onClick={() => {
                                                if (planOptionalFeaturesInput.trim()) {
                                                    const newFeatures = [...(planOptionalDetails.features || []), planOptionalFeaturesInput.trim()];
                                                    setPlanOptionalDetails({ ...planOptionalDetails, features: newFeatures });
                                                    setPlanOptionalFeaturesInput("");
                                                }
                                            }}>
                                                Add
                                            </Button>
                                        </InputGroup>
                                        {planOptionalDetails.features && planOptionalDetails.features.length > 0 && (
                                            <div className="mt-2" style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                                                {planOptionalDetails.features.map((feature: string, index: number) => (
                                                    <Badge key={index} color="primary" className="d-inline-flex align-items-center" style={{ fontSize: "0.875rem", padding: "0.5rem", maxWidth: "100%", wordBreak: "break-word", whiteSpace: "normal", textAlign: "left", justifyContent: "flex-start" }}>
                                                        <span style={{ maxWidth: "calc(100% - 20px)", wordBreak: "break-word", textAlign: "left" }}>{feature}</span>
                                                        <X
                                                            size={14}
                                                            className="ms-2"
                                                            style={{ cursor: "pointer", flexShrink: 0 }}
                                                            onClick={() => {
                                                                const currentFeatures = planOptionalDetails.features || [];
                                                                const newFeatures = currentFeatures.filter((_: string, i: number) => i !== index);
                                                                setPlanOptionalDetails({ ...planOptionalDetails, features: newFeatures });
                                                            }}
                                                        />
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                {planOptionalDetails && planOptionalDetails.id ? (
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <label className="form-label">Filters</label>
                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => { togglePlanOptionalModal(); openProductFiltersModal(planOptionalDetails, "planoptionals"); }}
                                                >
                                                    Filters
                                                </button>
                                            </div>
                                        </div>
                                    </Col>
                                ) : (
                                    <>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Categories <span className="text-danger">*</span>
                                                </label>
                                                <CategoryMultiSelect
                                                    selectedIds={planOptionalDetails.categories || []}
                                                    maxVisibleItems={4}
                                                    onChange={(ids) => setPlanOptionalDetails({ ...planOptionalDetails, categories: ids })}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <label className="form-label">Partners</label>
                                                <PartnerMultiSelect
                                                    selectedPartners={planOptionalDetails.partners || []}
                                                    maxVisibleItems={4}
                                                    onChange={(partners) => setPlanOptionalDetails({ ...planOptionalDetails, partners })}
                                                />
                                                <small className="form-text text-muted">
                                                    Optional. If none are selected, this product is not assigned to any partner. {partnerPickNote}
                                                </small>
                                            </div>
                                        </Col>
                                    </>
                                )}
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Parent Plans{" "}
                                            <Info 
                                                id="optional-parent-plans-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="optional-parent-plans-info">
                                            Select which plans this plan optional can be purchased with. Leave empty to show with all plans.
                                        </UncontrolledTooltip>
                                        <PlanMultiSelect
                                            selectedIds={planOptionalDetails.parent_plans || []}
                                            maxVisibleItems={4}
                                            onChange={(ids) => setPlanOptionalDetails({ ...planOptionalDetails, parent_plans: ids })}
                                        />
                                        <small className="form-text text-muted">
                                            Optional. If no plans are selected, this plan optional will be available with all plans.
                                        </small>
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Group <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="optional-group-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="optional-group-info">
                                            Categorize similar optionals together
                                        </UncontrolledTooltip>
                                        <select className="form-control" value={isNewGroup ? '__new__' : planOptionalGroupInput || ''} onChange={e => {
                                            if (e.target.value === '__new__') { setIsNewGroup(true); setPlanOptionalGroupInput(''); }
                                            else { setIsNewGroup(false); setPlanOptionalGroupInput(e.target.value); setPlanOptionalDetails({ ...planOptionalDetails, group: e.target.value }); }
                                        }}>
                                            <option value="">Select group</option>
                                            {planOptionalGroups.map(group => <option key={group} value={group}>{group}</option>)}
                                            <option value="__new__">Add new group...</option>
                                        </select>
                                        {isNewGroup && (
                                            <input type="text" className="form-control mt-2" placeholder="New group name *" value={planOptionalGroupInput} onChange={e => { setPlanOptionalGroupInput(e.target.value); setPlanOptionalDetails({ ...planOptionalDetails, group: e.target.value }); }} />
                                        )}
                                    </div>
                                </Col>
                                <Col lg={6}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Subscription Period{" "}
                                            <Info 
                                                id="optional-period-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="optional-period-info">
                                            Define when this optional expires or renews. Leave empty for one-time purchase (Once).
                                        </UncontrolledTooltip>
                                        <select
                                            className="form-control"
                                            value={planOptionalDetails.validity || ''}
                                            onChange={e => setPlanOptionalDetails({ ...planOptionalDetails, validity: e.target.value === '' ? '' : e.target.value })}
                                        >
                                            <option value="">Once</option>
                                            <option value="day">Day</option>
                                            <option value="week">Week</option>
                                            <option value="month">Month</option>
                                            <option value="year">Year</option>
                                            <option value="lifetime">Lifetime</option>
                                            <option value="custom">Custom</option>
                                        </select>
                                        {planOptionalDetails.validity === 'custom' && (
                                            <input
                                                type="number"
                                                className="form-control mt-2"
                                                placeholder="Enter number of days *"
                                                min="1"
                                                value={planOptionalDetails.validity_time || ''}
                                                onChange={e => setPlanOptionalDetails({ ...planOptionalDetails, validity_time: parseInt(e.target.value) })}
                                            />
                                        )}
                                    </div>
                                </Col>
                                <Col lg={6}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Cancellation Days{" "}
                                            <Info 
                                                id="optional-cancellation-days-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="optional-cancellation-days-info">
                                            Number of days after purchase when this product can be cancelled or refunded
                                        </UncontrolledTooltip>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={planOptionalDetails.cancellation_days ?? 5}
                                            onChange={e => {
                                                const val = parseInt(e.target.value);
                                                setPlanOptionalDetails({ ...planOptionalDetails, cancellation_days: isNaN(val) ? 5 : val });
                                            }}
                                            placeholder="5"
                                            min="0"
                                        />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="d-flex justify-content-end">
                                        <button className="btn btn-primary mb-3" type="button" onClick={async () => {
                                            const ok = planOptionalDetails && planOptionalDetails.id
                                                ? await handlePlanOptional("update")
                                                : await handlePlanOptional("create");
                                            if (ok) togglePlanOptionalModal();
                                        }}>{planOptionalDetails && planOptionalDetails.id ? "Update Optional" : "Add Optional"}</button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </Modal>

                {/* Delete Plan Optional Modal */}
                <Modal isOpen={deletePlanOptionalModal} toggle={toggleDeletePlanOptionalModal} centered className="rounded-4">
                    <ModalHeader className="py-3 px-4" toggle={toggleDeletePlanOptionalModal}>
                        <div className="modal-title mt-0 font-size-16 text-dark">Delete Plan Optional</div>
                    </ModalHeader>
                    <div className="modal-body">
                        <p>Are you sure you want to delete this plan optional?</p>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-sm btn-soft-dark" onClick={toggleDeletePlanOptionalModal}>Cancel</button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => { handlePlanOptional("delete"); toggleDeletePlanOptionalModal(); }}>Delete</button>
                    </div>
                </Modal>

                {/* Add/Edit Add-on Modal */}
                <Modal isOpen={addOnModal} size="md" toggle={toggleAddOnModal} backdrop={'static'} centered>
                    <ModalHeader toggle={toggleAddOnModal}>
                        <div className="modal-title mt-0 font-size-16 text-dark">
                            {addOnDetails && addOnDetails.id ? "Edit Add-on" : "Add Add-on"}
                        </div>
                    </ModalHeader>
                    <div className="modal-body">
                        <form>
                            <Row>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Title <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="addon-title-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="addon-title-info">
                                            Name of this add-on product
                                        </UncontrolledTooltip>
                                        <input type="text" className="form-control" value={addOnDetails.title || ''} onChange={e => setAddOnDetails({ ...addOnDetails, title: e.target.value })} placeholder="Enter title" />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Price (USD){" "}
                                            <Info 
                                                id="addon-price-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="addon-price-info">
                                            One-time or recurring price in USD. Leave empty for "Contact for pricing"
                                        </UncontrolledTooltip>
                                        <input type="number" className="form-control" value={addOnDetails.price ?? ''} onChange={e => setAddOnDetails({ ...addOnDetails, price: e.target.value })} placeholder="Leave empty for contact pricing" min="0" step="0.01" />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Description <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="addon-description-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="addon-description-info">
                                            Explain what this add-on provides to users
                                        </UncontrolledTooltip>
                                        <textarea className="form-control" value={addOnDetails.description || ''} onChange={e => setAddOnDetails({ ...addOnDetails, description: e.target.value })} placeholder="Enter description" />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Features (Optional){" "}
                                            <Info 
                                                id="addon-features-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="addon-features-info">
                                            List the key features included. Enter a feature and press Enter or click Add.
                                        </UncontrolledTooltip>
                                        <InputGroup>
                                            <Input
                                                type="text"
                                                value={addOnFeaturesInput}
                                                onChange={(e) => setAddOnFeaturesInput(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        if (addOnFeaturesInput.trim()) {
                                                            const newFeatures = [...(addOnDetails.features || []), addOnFeaturesInput.trim()];
                                                            setAddOnDetails({ ...addOnDetails, features: newFeatures });
                                                            setAddOnFeaturesInput("");
                                                        }
                                                    }
                                                }}
                                                placeholder="Enter a feature and press Enter"
                                            />
                                            <Button color="primary" onClick={() => {
                                                if (addOnFeaturesInput.trim()) {
                                                    const newFeatures = [...(addOnDetails.features || []), addOnFeaturesInput.trim()];
                                                    setAddOnDetails({ ...addOnDetails, features: newFeatures });
                                                    setAddOnFeaturesInput("");
                                                }
                                            }}>
                                                Add
                                            </Button>
                                        </InputGroup>
                                        {addOnDetails.features && addOnDetails.features.length > 0 && (
                                            <div className="mt-2" style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                                                {addOnDetails.features.map((feature: string, index: number) => (
                                                    <Badge key={index} color="primary" className="d-inline-flex align-items-center" style={{ fontSize: "0.875rem", padding: "0.5rem", maxWidth: "100%", wordBreak: "break-word", whiteSpace: "normal", textAlign: "left", justifyContent: "flex-start" }}>
                                                        <span style={{ maxWidth: "calc(100% - 20px)", wordBreak: "break-word", textAlign: "left" }}>{feature}</span>
                                                        <X
                                                            size={14}
                                                            className="ms-2"
                                                            style={{ cursor: "pointer", flexShrink: 0 }}
                                                            onClick={() => {
                                                                const currentFeatures = addOnDetails.features || [];
                                                                const newFeatures = currentFeatures.filter((_: string, i: number) => i !== index);
                                                                setAddOnDetails({ ...addOnDetails, features: newFeatures });
                                                            }}
                                                        />
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                {addOnDetails && addOnDetails.id ? (
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <label className="form-label">Filters</label>
                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => { toggleAddOnModal(); openProductFiltersModal(addOnDetails, "addon"); }}
                                                >
                                                    Filters
                                                </button>
                                            </div>
                                        </div>
                                    </Col>
                                ) : (
                                    <>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Categories <span className="text-danger">*</span>
                                                </label>
                                                <CategoryMultiSelect
                                                    selectedIds={addOnDetails.categories || []}
                                                    maxVisibleItems={4}
                                                    onChange={(ids) => setAddOnDetails({ ...addOnDetails, categories: ids })}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <label className="form-label">Partners</label>
                                                <PartnerMultiSelect
                                                    selectedPartners={addOnDetails.partners || []}
                                                    maxVisibleItems={4}
                                                    onChange={(partners) => setAddOnDetails({ ...addOnDetails, partners })}
                                                />
                                                <small className="form-text text-muted">
                                                    Optional. If none are selected, this product is not assigned to any partner. {partnerPickNote}
                                                </small>
                                            </div>
                                        </Col>
                                    </>
                                )}
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Parent Plans{" "}
                                            <Info 
                                                id="addon-parent-plans-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="addon-parent-plans-info">
                                            Select which plans this add-on can be purchased with. Leave empty to show with all plans.
                                        </UncontrolledTooltip>
                                        <PlanMultiSelect
                                            selectedIds={addOnDetails.parent_plans || []}
                                            maxVisibleItems={4}
                                            onChange={(ids) => setAddOnDetails({ ...addOnDetails, parent_plans: ids })}
                                        />
                                        <small className="form-text text-muted">
                                            Optional. If no plans are selected, this add-on will be available with all plans.
                                        </small>
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3 form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="addon-quantifiable"
                                            checked={!!addOnDetails.quantifiable}
                                            onChange={e => setAddOnDetails({ ...addOnDetails, quantifiable: e.target.checked })}
                                        />
                                        <label className="form-check-label" htmlFor="addon-quantifiable">
                                            Quantifiable{" "}
                                            <Info 
                                                id="quantifiable-info-icon" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="quantifiable-info-icon">
                                            Gives user ability to choose quantity of the product
                                        </UncontrolledTooltip>
                                    </div>
                                </Col>
                                <Col lg={6}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Expires After <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="addon-expires-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="addon-expires-info">
                                            Set the validity period for this add-on
                                        </UncontrolledTooltip>
                                        <select
                                            className="form-control"
                                            value={addOnDetails.validity || 'month'}
                                            onChange={e => setAddOnDetails({ ...addOnDetails, validity: e.target.value })}
                                        >
                                            <option value="day">Day</option>
                                            <option value="week">Week</option>
                                            <option value="month">Month</option>
                                            <option value="year">Year</option>
                                            <option value="lifetime">Lifetime</option>
                                            <option value="custom">Custom</option>
                                        </select>
                                        {addOnDetails.validity === 'custom' && (
                                            <input
                                                type="number"
                                                className="form-control mt-2"
                                                placeholder="Enter number of days *"
                                                min="1"
                                                value={addOnDetails.validity_time || ''}
                                                onChange={e => setAddOnDetails({ ...addOnDetails, validity_time: parseInt(e.target.value) })}
                                            />
                                        )}
                                    </div>
                                </Col>
                                <Col lg={6}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Cancellation Days{" "}
                                            <Info 
                                                id="addon-cancellation-days-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="addon-cancellation-days-info">
                                            Number of days after purchase when this product can be cancelled or refunded
                                        </UncontrolledTooltip>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={addOnDetails.cancellation_days ?? 5}
                                            onChange={e => {
                                                const val = parseInt(e.target.value);
                                                setAddOnDetails({ ...addOnDetails, cancellation_days: isNaN(val) ? 5 : val });
                                            }}
                                            placeholder="5"
                                            min="0"
                                        />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="d-flex justify-content-end">
                                        <button className="btn btn-primary mb-3" type="button" onClick={async () => {
                                            const ok = addOnDetails && addOnDetails.id
                                                ? await handleAddOn("update")
                                                : await handleAddOn("create");
                                            if (ok) toggleAddOnModal();
                                        }}>{addOnDetails && addOnDetails.id ? "Update Add-on" : "Add Add-on"}</button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </Modal>

                {/* Delete Add-on Modal */}
                <Modal isOpen={deleteAddOnModal} toggle={toggleDeleteAddOnModal} centered className="rounded-4">
                    <ModalHeader className="py-3 px-4" toggle={toggleDeleteAddOnModal}>
                        <div className="modal-title mt-0 font-size-16 text-dark">Delete Add-on</div>
                    </ModalHeader>
                    <div className="modal-body">
                        <p>Are you sure you want to delete this add-on?</p>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-sm btn-soft-dark" onClick={toggleDeleteAddOnModal}>Cancel</button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => { handleAddOn("delete"); toggleDeleteAddOnModal(); }}>Delete</button>
                    </div>
                </Modal>

                <Row className="mt-5">
          <Col md={6}>
            <div className="d-flex align-items-center mb-3 justify-content-between">
              <h4 style={{fontWeight: 700, color: '#222', marginBottom: 0}}>Add-ons</h4>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                onClick={() => { const defId = getDefaultCategoryId(); setAddOnDetails({ pricing_type: 'fixed', categories: defId ? [defId] : [], partners: [], parent_plans: [], features: [] }); setAddOnFeaturesInput(""); toggleAddOnModal(); }}
              >
                <i className="bx bx-plus" style={{marginRight: 4}}></i> Add Add-on
              </button>
            </div>
            <div style={{width: '100%', marginTop: 0}}>
              <div className="row g-3" style={{margin: 0}}>
                {addOns.length === 0 && (
                  <div className="text-muted" style={{padding: 24}}>No add-ons found.</div>
                )}
                {addOns.map((addon: any, addonIdx: number) => (
                  <div className="col-12 col-md-6" key={addon.id}>
                    <div style={{
                      background: '#fff',
                      borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      padding: 20,
                      position: 'relative',
                      minHeight: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #e0e0e0',
                      overflow: 'hidden',
                    }}>
                      <div style={{position: 'absolute', top: 12, left: 12, display: 'flex', gap: 4, zIndex: 10}}>
                        <button 
                          className="btn btn-link p-0" 
                          style={{color: addonIdx === 0 ? '#ccc' : '#1976d2'}} 
                          onClick={() => addonIdx !== 0 && movePriority('addon', addon.id, 'up')}
                          disabled={addonIdx === 0}
                          title="Move up"
                        >
                          <i className="bx bx-chevron-up" style={{fontSize: 20}}></i>
                        </button>
                        <button 
                          className="btn btn-link p-0" 
                          style={{color: addonIdx === addOns.length - 1 ? '#ccc' : '#1976d2'}} 
                          onClick={() => addonIdx !== addOns.length - 1 && movePriority('addon', addon.id, 'down')}
                          disabled={addonIdx === addOns.length - 1}
                          title="Move down"
                        >
                          <i className="bx bx-chevron-down" style={{fontSize: 20}}></i>
                        </button>
                      </div>
                      <div style={{position: 'absolute', top: 12, right: 12, display: 'flex', gap: 8, zIndex: 10}}>
                        <button className="btn btn-link p-0" style={{color: '#1976d2'}} onClick={() => { setAddOnDetails({ ...addon, categories: extractCategoryIds(addon), partners: extractPartnersWithShare(addon), parent_plans: extractParentPlanIds(addon), features: addon.features || [] }); setAddOnFeaturesInput(""); toggleAddOnModal(); }} title="Edit">
                          <i className="bx bx-edit-alt" style={{fontSize: 20}}></i>
                        </button>
                        <button className="btn btn-link p-0" style={{color: '#1976d2'}} onClick={() => openProductFiltersModal({ ...addon, categories: extractCategoryIds(addon) }, "addon")} title="Filters">
                          <i className="bx bx-filter-alt" style={{fontSize: 20}}></i>
                        </button>
                        <button className="btn btn-link p-0" style={{color: '#d32f2f'}} onClick={() => { setAddOnDetails({ ...addon, categories: extractCategoryIds(addon) }); toggleDeleteAddOnModal(); }} title="Delete">
                          <i className="bx bx-trash" style={{fontSize: 20}}></i>
                        </button>
                      </div>
                                      <div style={{paddingTop: 8, overflow: 'hidden'}}>
                                        <div style={{fontWeight: 700, fontSize: 18, color: '#222', marginBottom: 4}}>{addon.title}</div>
                                        <div style={{fontWeight: 600, fontSize: 16, color: '#1976d2', marginBottom: 4}}>
                                            {addon.price !== null && addon.price !== undefined ? (
                                                <>
                                                    ${formatPrice(addon.price)}
                                                    {addon.validity && addon.validity !== '' && addon.validity !== 'lifetime' && addon.validity !== 'custom' && (
                                                        <span style={{fontWeight: 400, fontSize: 13, color: '#888'}}> / {addon.validity}</span>
                                                    )}
                                                </>
                                            ) : (
                                                'Contact for pricing'
                                            )}
                                        </div>
                                        <div style={{fontSize: 13, color: '#555', marginBottom: 8}}>
                                            <ReactMarkdown
                                                components={{
                                                    p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 13, color: '#555' }}>{children}</p>,
                                                    strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                                    em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                    ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ul>,
                                                    ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ol>,
                                                    li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 13, color: '#555' }}>{children}</li>,
                                                    a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                                }}
                                            >
                                                {cleanMarkdown(addon.description)}
                                            </ReactMarkdown>
                                        </div>
                                        <div style={{fontSize: 12, color: '#888', marginBottom: 4}}>
                                          Validity: {addon.validity === 'custom' ? `${addon.validity_time} days` : (addon.validity ? addon.validity.charAt(0).toUpperCase() + addon.validity.slice(1) : 'Month')}
                                        </div>
                                        <div style={{fontSize: 12, color: addon.quantifiable ? '#388e3c' : '#888', marginBottom: 8}}>
                                          {addon.quantifiable ? <><i className="bx bx-check-circle" style={{color: '#388e3c', marginRight: 2}}></i>Quantifiable</> : 'Not quantifiable'}
                                        </div>

                                        {/* Features Section */}
                                        {addon.features && addon.features.length > 0 && (
                                          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8e8e8' }}>
                                            <div style={{ fontSize: "0.75rem", color: "#6c757d", fontWeight: 600, marginBottom: 8 }}>
                                              Features
                                            </div>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                              {addon.features.map((feature: string, fIdx: number) => (
                                                <Badge
                                                  key={fIdx}
                                                  color="light"
                                                  className="px-2 py-1"
                                                  style={{
                                                    fontSize: "0.75rem",
                                                    color: "#495057",
                                                    border: "1px solid #dee2e6",
                                                    fontWeight: 400,
                                                    maxWidth: "100%",
                                                    wordBreak: "break-word",
                                                    whiteSpace: "normal",
                                                    textAlign: "left",
                                                  }}
                                                >
                                                  {feature}
                                                </Badge>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        <div style={{ marginTop: 8 }}>
                                            <FiltersChips item={addon} />
                                        </div>
                                        
                                        {/* Stripe Check Section */}
                                        {(addon.strp_prod_id || addon.strp_price_id) && (
                                          <div style={{marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8e8e8'}}>
                                            <button
                                              className="btn btn-sm btn-outline-primary"
                                              onClick={() => checkStripeMode(addon, addon.id, 'addon')}
                                              disabled={checkingStripe[`addon_${addon.id}`]}
                                              style={{fontSize: 11, padding: '4px 10px', marginBottom: 6}}
                                            >
                                              {checkingStripe[`addon_${addon.id}`] ? 'Checking...' : 'Check Stripe'}
                                            </button>
                                            {renderStripeCheckResult(`addon_${addon.id}`, addon.id, 'addon')}
                                          </div>
                                        )}
                                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Col>
          <Col md={6} style={{borderLeft: '1px solid #e0e0e0', paddingLeft: 8}}>
            <div className="d-flex align-items-center mb-3 justify-content-between">
              <h4 style={{fontWeight: 700, color: '#222', marginBottom: 0}}>Subscription Add-ons</h4>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                onClick={() => { const defId = getDefaultCategoryId(); setSubscriptionAddOnDetails({ pricing_type: 'fixed', categories: defId ? [defId] : [], partners: [], parent_plans: [], features: [] }); setSubscriptionAddOnFeaturesInput(""); toggleSubscriptionAddOnModal(); }}
              >
                <i className="bx bx-plus" style={{marginRight: 4}}></i> Add Subscription Add-on
              </button>
            </div>
            <div style={{width: '100%', marginTop: 0}}>
              <div className="row g-3" style={{margin: 0}}>
                {subscriptionAddOns.length === 0 && (
                  <div className="text-muted" style={{padding: 24}}>No subscription add-ons found.</div>
                )}
                {subscriptionAddOns.map((addon: any, subAddonIdx: number) => (
                  <div className="col-12 col-md-6" key={addon.id}>
                    <div style={{
                      background: '#fff',
                      borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      padding: 20,
                      position: 'relative',
                      minHeight: 200,
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #e0e0e0',
                      overflow: 'hidden',
                    }}>
                      <div style={{position: 'absolute', top: 12, left: 12, display: 'flex', gap: 4, zIndex: 10}}>
                        <button 
                          className="btn btn-link p-0" 
                          style={{color: subAddonIdx === 0 ? '#ccc' : '#1976d2'}} 
                          onClick={() => subAddonIdx !== 0 && movePriority('subscriptionaddon', addon.id, 'up')}
                          disabled={subAddonIdx === 0}
                          title="Move up"
                        >
                          <i className="bx bx-chevron-up" style={{fontSize: 20}}></i>
                        </button>
                        <button 
                          className="btn btn-link p-0" 
                          style={{color: subAddonIdx === subscriptionAddOns.length - 1 ? '#ccc' : '#1976d2'}} 
                          onClick={() => subAddonIdx !== subscriptionAddOns.length - 1 && movePriority('subscriptionaddon', addon.id, 'down')}
                          disabled={subAddonIdx === subscriptionAddOns.length - 1}
                          title="Move down"
                        >
                          <i className="bx bx-chevron-down" style={{fontSize: 20}}></i>
                        </button>
                      </div>
                      <div style={{position: 'absolute', top: 12, right: 12, display: 'flex', gap: 8, zIndex: 10}}>
                        <button className="btn btn-link p-0" style={{color: '#1976d2'}} onClick={() => { setSubscriptionAddOnDetails({ ...addon, categories: extractCategoryIds(addon), partners: extractPartnersWithShare(addon), parent_plans: extractParentPlanIds(addon), features: addon.features || [] }); setSubscriptionAddOnFeaturesInput(""); toggleSubscriptionAddOnModal(); }} title="Edit">
                          <i className="bx bx-edit-alt" style={{fontSize: 20}}></i>
                        </button>
                        <button className="btn btn-link p-0" style={{color: '#1976d2'}} onClick={() => openProductFiltersModal({ ...addon, categories: extractCategoryIds(addon) }, "subscriptionaddon")} title="Filters">
                          <i className="bx bx-filter-alt" style={{fontSize: 20}}></i>
                        </button>
                        <button className="btn btn-link p-0" style={{color: '#d32f2f'}} onClick={() => { setSubscriptionAddOnDetails({ ...addon, categories: extractCategoryIds(addon) }); toggleDeleteSubscriptionAddOnModal(); }} title="Delete">
                          <i className="bx bx-trash" style={{fontSize: 20}}></i>
                        </button>
                      </div>
                                      <div style={{paddingTop: 8, overflow: 'hidden'}}>
                                        <div style={{fontWeight: 700, fontSize: 18, color: '#222', marginBottom: 4}}>{addon.title}</div>
                                        <div style={{fontWeight: 600, fontSize: 16, color: '#1976d2', marginBottom: 4}}>
                                            {addon.price !== null && addon.price !== undefined ? (
                                                <>${formatPrice(addon.price)} / {addon.period ? addon.period.charAt(0).toUpperCase() + addon.period.slice(1) : 'Month'}</>
                                            ) : (
                                                'Contact for pricing'
                                            )}
                                        </div>
                                        <div style={{fontSize: 13, color: '#555', marginBottom: 8}}>
                                            <ReactMarkdown
                                                components={{
                                                    p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 13, color: '#555' }}>{children}</p>,
                                                    strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                                    em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                    ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ul>,
                                                    ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ol>,
                                                    li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 13, color: '#555' }}>{children}</li>,
                                                    a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                                }}
                                            >
                                                {cleanMarkdown(addon.description)}
                                            </ReactMarkdown>
                                        </div>

                                        {/* Features Section */}
                                        {addon.features && addon.features.length > 0 && (
                                          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8e8e8' }}>
                                            <div style={{ fontSize: "0.75rem", color: "#6c757d", fontWeight: 600, marginBottom: 8 }}>
                                              Features
                                            </div>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                              {addon.features.map((feature: string, fIdx: number) => (
                                                <Badge
                                                  key={fIdx}
                                                  color="light"
                                                  className="px-2 py-1"
                                                  style={{
                                                    fontSize: "0.75rem",
                                                    color: "#495057",
                                                    border: "1px solid #dee2e6",
                                                    fontWeight: 400,
                                                    maxWidth: "100%",
                                                    wordBreak: "break-word",
                                                    whiteSpace: "normal",
                                                    textAlign: "left",
                                                  }}
                                                >
                                                  {feature}
                                                </Badge>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        <div style={{ marginTop: 8 }}>
                                            <FiltersChips item={addon} />
                                        </div>
                                        
                                        {/* Stripe Check Section */}
                                        {(addon.strp_prod_id || addon.strp_price_id) && (
                                          <div style={{marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8e8e8'}}>
                                            <button
                                              className="btn btn-sm btn-outline-primary"
                                              onClick={() => checkStripeMode(addon, addon.id, 'subaddon')}
                                              disabled={checkingStripe[`subaddon_${addon.id}`]}
                                              style={{fontSize: 11, padding: '4px 10px', marginBottom: 6}}
                                            >
                                              {checkingStripe[`subaddon_${addon.id}`] ? 'Checking...' : 'Check Stripe'}
                                            </button>
                                            {renderStripeCheckResult(`subaddon_${addon.id}`, addon.id, 'subaddon')}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Col>
                        </Row>

                {/* Add/Edit Subscription Add-on Modal */}
                <Modal isOpen={subscriptionAddOnModal} size="md" toggle={toggleSubscriptionAddOnModal} backdrop={'static'} centered>
                  <ModalHeader toggle={toggleSubscriptionAddOnModal}>
                    <div className="modal-title mt-0 font-size-16 text-dark">
                      {subscriptionAddOnDetails && subscriptionAddOnDetails.id ? "Edit Subscription Add-on" : "Add Subscription Add-on"}
                    </div>
                  </ModalHeader>
                  <div className="modal-body">
                    <form>
                      <Row>
                        <Col lg={12}>
                          <div className="mb-3">
                            <label className="form-label">
                              Title <span className="text-danger">*</span>{" "}
                              <Info 
                                id="subaddon-title-info" 
                                size={16} 
                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                              />
                            </label>
                            <UncontrolledTooltip placement="top" target="subaddon-title-info">
                              Name of this subscription add-on
                            </UncontrolledTooltip>
                            <input type="text" className="form-control" value={subscriptionAddOnDetails.title || ''} onChange={e => setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, title: e.target.value })} placeholder="Enter title" />
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="mb-3">
                            <label className="form-label">
                              Price (USD){" "}
                              <Info 
                                id="subaddon-price-info" 
                                size={16} 
                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                              />
                            </label>
                            <UncontrolledTooltip placement="top" target="subaddon-price-info">
                              Recurring price in USD for this subscription add-on. Leave empty for "Contact for pricing"
                            </UncontrolledTooltip>
                            <input type="number" className="form-control" value={subscriptionAddOnDetails.price ?? ''} onChange={e => setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, price: e.target.value })} placeholder="Leave empty for contact pricing" min="0" step="0.01" />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="mb-3">
                            <label className="form-label">
                              Subscription Period <span className="text-danger">*</span>{" "}
                              <Info 
                                id="subaddon-period-info" 
                                size={16} 
                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                              />
                            </label>
                            <UncontrolledTooltip placement="top" target="subaddon-period-info">
                              Define how often this subscription add-on renews
                            </UncontrolledTooltip>
                            <select
                              className="form-control"
                              value={subscriptionAddOnDetails.validity || 'month'}
                              onChange={e => setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, validity: e.target.value, period: e.target.value })}
                            >
                              <option value="day">Day</option>
                              <option value="week">Week</option>
                              <option value="month">Month</option>
                              <option value="quarter">Quarter</option>
                              <option value="year">Year</option>
                              <option value="custom">Custom</option>
                            </select>
                            {subscriptionAddOnDetails.validity === 'custom' && (
                              <input
                                type="number"
                                className="form-control mt-2"
                                placeholder="Enter number of days *"
                                min="1"
                                value={subscriptionAddOnDetails.validity_time || ''}
                                onChange={e => setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, validity_time: parseInt(e.target.value) })}
                              />
                            )}
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="mb-3">
                            <label className="form-label">
                              Cancellation Days{" "}
                              <Info 
                                id="subaddon-cancellation-days-info" 
                                size={16} 
                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                              />
                            </label>
                            <UncontrolledTooltip placement="top" target="subaddon-cancellation-days-info">
                              Number of days after purchase when this product can be cancelled or refunded
                            </UncontrolledTooltip>
                            <input
                              type="number"
                              className="form-control"
                              value={subscriptionAddOnDetails.cancellation_days ?? 5}
                              onChange={e => {
                                const val = parseInt(e.target.value);
                                setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, cancellation_days: isNaN(val) ? 5 : val });
                              }}
                              placeholder="5"
                              min="0"
                            />
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="mb-3">
                            <label className="form-label">
                              Description <span className="text-danger">*</span>{" "}
                              <Info 
                                id="subaddon-description-info" 
                                size={16} 
                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                              />
                            </label>
                            <UncontrolledTooltip placement="top" target="subaddon-description-info">
                              Explain what this subscription add-on provides
                            </UncontrolledTooltip>
                            <textarea className="form-control" value={subscriptionAddOnDetails.description || ''} onChange={e => setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, description: e.target.value })} placeholder="Enter description" />
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="mb-3">
                            <label className="form-label">
                              Features (Optional){" "}
                              <Info 
                                id="subaddon-features-info" 
                                size={16} 
                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                              />
                            </label>
                            <UncontrolledTooltip placement="top" target="subaddon-features-info">
                              List the key features included. Enter a feature and press Enter or click Add.
                            </UncontrolledTooltip>
                            <InputGroup>
                              <Input
                                type="text"
                                value={subscriptionAddOnFeaturesInput}
                                onChange={(e) => setSubscriptionAddOnFeaturesInput(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (subscriptionAddOnFeaturesInput.trim()) {
                                      const newFeatures = [...(subscriptionAddOnDetails.features || []), subscriptionAddOnFeaturesInput.trim()];
                                      setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, features: newFeatures });
                                      setSubscriptionAddOnFeaturesInput("");
                                    }
                                  }
                                }}
                                placeholder="Enter a feature and press Enter"
                              />
                              <Button color="primary" onClick={() => {
                                if (subscriptionAddOnFeaturesInput.trim()) {
                                  const newFeatures = [...(subscriptionAddOnDetails.features || []), subscriptionAddOnFeaturesInput.trim()];
                                  setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, features: newFeatures });
                                  setSubscriptionAddOnFeaturesInput("");
                                }
                              }}>
                                Add
                              </Button>
                            </InputGroup>
                            {subscriptionAddOnDetails.features && subscriptionAddOnDetails.features.length > 0 && (
                              <div className="mt-2" style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                                {subscriptionAddOnDetails.features.map((feature: string, index: number) => (
                                  <Badge key={index} color="primary" className="d-inline-flex align-items-center" style={{ fontSize: "0.875rem", padding: "0.5rem", maxWidth: "100%", wordBreak: "break-word", whiteSpace: "normal", textAlign: "left", justifyContent: "flex-start" }}>
                                    <span style={{ maxWidth: "calc(100% - 20px)", wordBreak: "break-word", textAlign: "left" }}>{feature}</span>
                                    <X
                                      size={14}
                                      className="ms-2"
                                      style={{ cursor: "pointer", flexShrink: 0 }}
                                      onClick={() => {
                                        const currentFeatures = subscriptionAddOnDetails.features || [];
                                        const newFeatures = currentFeatures.filter((_: string, i: number) => i !== index);
                                        setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, features: newFeatures });
                                      }}
                                    />
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </Col>
                        {subscriptionAddOnDetails && subscriptionAddOnDetails.id ? (
                          <Col lg={12}>
                            <div className="mb-3">
                              <label className="form-label">Filters</label>
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => { toggleSubscriptionAddOnModal(); openProductFiltersModal(subscriptionAddOnDetails, "subscriptionaddon"); }}
                                >
                                  Filters
                                </button>
                              </div>
                            </div>
                          </Col>
                        ) : (
                          <>
                            <Col lg={6}>
                              <div className="mb-3">
                                <label className="form-label">
                                  Categories <span className="text-danger">*</span>
                                </label>
                                <CategoryMultiSelect
                                  selectedIds={subscriptionAddOnDetails.categories || []}
                                  maxVisibleItems={4}
                                  onChange={(ids) => setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, categories: ids })}
                                />
                              </div>
                            </Col>
                            <Col lg={6}>
                              <div className="mb-3">
                                <label className="form-label">Partners</label>
                                <PartnerMultiSelect
                                  selectedPartners={subscriptionAddOnDetails.partners || []}
                                  maxVisibleItems={4}
                                  onChange={(partners) => setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, partners })}
                                />
                                <small className="form-text text-muted">
                                  Optional. If none are selected, this product is not assigned to any partner. {partnerPickNote}
                                </small>
                              </div>
                            </Col>
                          </>
                        )}
                        <Col lg={12}>
                          <div className="mb-3">
                            <label className="form-label">
                              Parent Plans{" "}
                              <Info 
                                id="subaddon-parent-plans-info" 
                                size={16} 
                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                              />
                            </label>
                            <UncontrolledTooltip placement="top" target="subaddon-parent-plans-info">
                              Select which plans this subscription add-on can be purchased with. Leave empty to show with all plans.
                            </UncontrolledTooltip>
                            <PlanMultiSelect
                              selectedIds={subscriptionAddOnDetails.parent_plans || []}
                              maxVisibleItems={4}
                              onChange={(ids) => setSubscriptionAddOnDetails({ ...subscriptionAddOnDetails, parent_plans: ids })}
                            />
                            <small className="form-text text-muted">
                              Optional. If no plans are selected, this subscription add-on will be available with all plans.
                            </small>
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="d-flex justify-content-end">
                            <button className="btn btn-primary mb-3" type="button" onClick={async () => {
                                const ok = subscriptionAddOnDetails && subscriptionAddOnDetails.id
                                    ? await handleSubscriptionAddOn("update")
                                    : await handleSubscriptionAddOn("create");
                                if (ok) toggleSubscriptionAddOnModal();
                            }}>{subscriptionAddOnDetails && subscriptionAddOnDetails.id ? "Update Subscription Add-on" : "Add Subscription Add-on"}</button>
                          </div>
                        </Col>
                      </Row>
                    </form>
                  </div>
                </Modal>

                {/* Delete Subscription Add-on Modal */}
                <Modal isOpen={deleteSubscriptionAddOnModal} toggle={toggleDeleteSubscriptionAddOnModal} centered className="rounded-4">
                  <ModalHeader className="py-3 px-4" toggle={toggleDeleteSubscriptionAddOnModal}>
                    <div className="modal-title mt-0 font-size-16 text-dark">Delete Subscription Add-on</div>
                  </ModalHeader>
                  <div className="modal-body">
                    <p>Are you sure you want to delete this subscription add-on?</p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-sm btn-soft-dark" onClick={toggleDeleteSubscriptionAddOnModal}>Cancel</button>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => { handleSubscriptionAddOn("delete"); toggleDeleteSubscriptionAddOnModal(); }}>Delete</button>
                  </div>
                </Modal>

                {/* One-Time Products Section */}
                <Row className="mt-5">
                  <Col md={12}>
                    <div className="d-flex align-items-center mb-3 justify-content-between">
                      <h4 style={{fontWeight: 700, color: '#222', marginBottom: 0}}>One-Time Products</h4>
                      <button
                        className="btn btn-primary btn-sm"
                        type="button"
                        onClick={() => { const defId = getDefaultCategoryId(); setOneTimeProductDetails({ pricing_type: 'fixed', categories: defId ? [defId] : [], partners: [], features: [] }); setOneTimeProductFeaturesInput(""); toggleOneTimeProductModal(); }}
                      >
                        <i className="bx bx-plus" style={{marginRight: 4}}></i> Add One-Time Product
                      </button>
                    </div>
                    <div style={{width: '100%', marginTop: 0}}>
                      <div className="row g-3" style={{margin: 0}}>
                        {oneTimeProducts.length === 0 && (
                          <div className="text-muted" style={{padding: 24}}>No one-time products found.</div>
                        )}
                        {oneTimeProducts.map((product: any, productIdx: number) => (
                          <div className="col-12 col-md-4 col-lg-3" key={product.id}>
                            <div style={{
                              background: '#fff',
                              borderRadius: 12,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                              padding: 20,
                              position: 'relative',
                              minHeight: 220,
                              display: 'flex',
                              flexDirection: 'column',
                              border: '1px solid #e0e0e0',
                              overflow: 'hidden',
                            }}>
                              <div style={{position: 'absolute', top: 12, left: 12, display: 'flex', gap: 4, zIndex: 10}}>
                                <button 
                                  className="btn btn-link p-0" 
                                  style={{color: productIdx === 0 ? '#ccc' : '#1976d2'}} 
                                  onClick={() => productIdx !== 0 && movePriority('onetimeproduct', product.id, 'up')}
                                  disabled={productIdx === 0}
                                  title="Move up"
                                >
                                  <i className="bx bx-chevron-up" style={{fontSize: 20}}></i>
                                </button>
                                <button 
                                  className="btn btn-link p-0" 
                                  style={{color: productIdx === oneTimeProducts.length - 1 ? '#ccc' : '#1976d2'}} 
                                  onClick={() => productIdx !== oneTimeProducts.length - 1 && movePriority('onetimeproduct', product.id, 'down')}
                                  disabled={productIdx === oneTimeProducts.length - 1}
                                  title="Move down"
                                >
                                  <i className="bx bx-chevron-down" style={{fontSize: 20}}></i>
                                </button>
                              </div>
                              <div style={{position: 'absolute', top: 12, right: 12, display: 'flex', gap: 8, zIndex: 10}}>
                                <button className="btn btn-link p-0" style={{color: '#1976d2'}} onClick={() => { setOneTimeProductDetails({ ...product, pricing_type: product.pricing_type || 'fixed', categories: extractCategoryIds(product), partners: extractPartnersWithShare(product), features: product.features || [] }); setOneTimeProductFeaturesInput(""); toggleOneTimeProductModal(); }} title="Edit">
                                  <i className="bx bx-edit-alt" style={{fontSize: 20}}></i>
                                </button>
                                <button className="btn btn-link p-0" style={{color: '#1976d2'}} onClick={() => openProductFiltersModal({ ...product, categories: extractCategoryIds(product) }, "onetimeproduct")} title="Filters">
                                  <i className="bx bx-filter-alt" style={{fontSize: 20}}></i>
                                </button>
                                <button className="btn btn-link p-0" style={{color: '#d32f2f'}} onClick={() => { setOneTimeProductDetails({ ...product, categories: extractCategoryIds(product) }); toggleDeleteOneTimeProductModal(); }} title="Delete">
                                  <i className="bx bx-trash" style={{fontSize: 20}}></i>
                                </button>
                              </div>
                              <div style={{paddingTop: 8, overflow: 'hidden', textAlign: 'left'}}>
                                <div style={{fontWeight: 700, fontSize: 18, color: '#222', marginBottom: 4}}>{product.title}</div>
                                <div style={{fontWeight: 600, fontSize: 16, color: '#1976d2', marginBottom: 4}}>
                                  {product.price !== null && product.price !== undefined ? `$${formatPrice(product.price)}` : 'Contact for pricing'}
                                </div>
                                <div style={{fontSize: 13, color: '#555', marginBottom: 8}}>
                                    <ReactMarkdown
                                        components={{
                                            p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 13, color: '#555' }}>{children}</p>,
                                            strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                            em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                            ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ul>,
                                            ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ol>,
                                            li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 13, color: '#555' }}>{children}</li>,
                                            a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                        }}
                                    >
                                        {cleanMarkdown(product.description)}
                                    </ReactMarkdown>
                                </div>
                                <div style={{fontSize: 12, color: product.quantifiable ? '#388e3c' : '#888', marginBottom: 8}}>
                                  {product.quantifiable ? <><i className="bx bx-check-circle" style={{color: '#388e3c', marginRight: 2}}></i>Quantifiable</> : 'Not quantifiable'}
                                </div>

                                {/* Features Section */}
                                {product.features && product.features.length > 0 && (
                                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8e8e8' }}>
                                    <div style={{ fontSize: "0.75rem", color: "#6c757d", fontWeight: 600, marginBottom: 8 }}>
                                      Features
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                      {product.features.map((feature: string, fIdx: number) => (
                                        <Badge
                                          key={fIdx}
                                          color="light"
                                          className="px-2 py-1"
                                          style={{
                                            fontSize: "0.75rem",
                                            color: "#495057",
                                            border: "1px solid #dee2e6",
                                            fontWeight: 400,
                                            maxWidth: "100%",
                                            wordBreak: "break-word",
                                            whiteSpace: "normal",
                                            textAlign: "left",
                                          }}
                                        >
                                          {feature}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div style={{ marginTop: 8 }}>
                                    <FiltersChips item={product} />
                                </div>
                                
                                {/* Stripe Check Section */}
                                {(product.strp_prod_id || product.strp_price_id) && (
                                  <div style={{marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8e8e8'}}>
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => checkStripeMode(product, product.id, 'onetimeproduct')}
                                      disabled={checkingStripe[`onetimeproduct_${product.id}`]}
                                      style={{fontSize: 11, padding: '4px 10px', marginBottom: 6}}
                                    >
                                      {checkingStripe[`onetimeproduct_${product.id}`] ? 'Checking...' : 'Check Stripe'}
                                    </button>
                                    {renderStripeCheckResult(`onetimeproduct_${product.id}`, product.id, 'onetimeproduct')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Add/Edit One-Time Product Modal */}
                <Modal isOpen={oneTimeProductModal} size="md" toggle={toggleOneTimeProductModal} backdrop={'static'} centered>
                    <ModalHeader toggle={toggleOneTimeProductModal}>
                        <div className="modal-title mt-0 font-size-16 text-dark">
                            {oneTimeProductDetails && oneTimeProductDetails.id ? "Edit One-Time Product" : "Add One-Time Product"}
                        </div>
                    </ModalHeader>
                    <div className="modal-body">
                        <form>
                            <Row>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Title <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="onetimeproduct-title-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="onetimeproduct-title-info">
                                            Name of this one-time product
                                        </UncontrolledTooltip>
                                        <input type="text" className="form-control" value={oneTimeProductDetails.title || ''} onChange={e => setOneTimeProductDetails({ ...oneTimeProductDetails, title: e.target.value })} placeholder="Enter title" />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Pricing Type <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="onetimeproduct-pricing-type-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="onetimeproduct-pricing-type-info">
                                            Choose how this product is priced: Fixed Price, Contact for Pricing, or Buy Externally
                                        </UncontrolledTooltip>
                                        <select
                                            className="form-control"
                                            value={oneTimeProductDetails.pricing_type || 'fixed'}
                                            onChange={e => {
                                                const pricingType = e.target.value;
                                                const updates: any = { pricing_type: pricingType };
                                                if (pricingType === 'contact') {
                                                    updates.price = null;
                                                    updates.external_url = null;
                                                } else if (pricingType === 'external') {
                                                    updates.external_url = oneTimeProductDetails.external_url || '';
                                                } else {
                                                    updates.external_url = null;
                                                }
                                                setOneTimeProductDetails({ ...oneTimeProductDetails, ...updates });
                                            }}
                                        >
                                            <option value="fixed">Fixed Price</option>
                                            <option value="contact">Contact for Pricing</option>
                                            <option value="external">Buy Externally</option>
                                        </select>
                                    </div>
                                </Col>
                                {oneTimeProductDetails.pricing_type === 'external' && (
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                External URL <span className="text-danger">*</span>{" "}
                                                <Info 
                                                    id="onetimeproduct-external-url-info" 
                                                    size={16} 
                                                    style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                                />
                                            </label>
                                            <UncontrolledTooltip placement="top" target="onetimeproduct-external-url-info">
                                                URL where customers will be redirected to purchase this product externally
                                            </UncontrolledTooltip>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={oneTimeProductDetails.external_url || ''}
                                                onChange={e => setOneTimeProductDetails({ ...oneTimeProductDetails, external_url: e.target.value })}
                                                placeholder="https://example.com/purchase"
                                                required
                                            />
                                        </div>
                                    </Col>
                                )}
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Price (USD){" "}
                                            {oneTimeProductDetails.pricing_type === 'fixed' && <span className="text-danger">*</span>}
                                            <Info 
                                                id="onetimeproduct-price-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="onetimeproduct-price-info">
                                            {oneTimeProductDetails.pricing_type === 'fixed' && 'One-time price in USD.'}
                                            {oneTimeProductDetails.pricing_type === 'external' && 'Set the price displayed to customers (optional for external links).'}
                                            {oneTimeProductDetails.pricing_type === 'contact' && 'Not applicable for "Contact for Pricing" option.'}
                                        </UncontrolledTooltip>
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            value={oneTimeProductDetails.price ?? ''} 
                                            onChange={e => {
                                                const value = e.target.value;
                                                // For external pricing, treat '0' as empty (optional price)
                                                if (oneTimeProductDetails.pricing_type === 'external' && value === '0') {
                                                    setOneTimeProductDetails({ ...oneTimeProductDetails, price: '' });
                                                } else {
                                                    setOneTimeProductDetails({ ...oneTimeProductDetails, price: value });
                                                }
                                            }} 
                                            placeholder={oneTimeProductDetails.pricing_type === 'contact' ? 'Not applicable' : 'Enter price'} 
                                            min="0" 
                                            step="0.01"
                                            disabled={oneTimeProductDetails.pricing_type === 'contact'}
                                            required={oneTimeProductDetails.pricing_type === 'fixed'}
                                        />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Description <span className="text-danger">*</span>{" "}
                                            <Info 
                                                id="onetimeproduct-description-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="onetimeproduct-description-info">
                                            Explain what this one-time product provides to users
                                        </UncontrolledTooltip>
                                        <textarea className="form-control" value={oneTimeProductDetails.description || ''} onChange={e => setOneTimeProductDetails({ ...oneTimeProductDetails, description: e.target.value })} placeholder="Enter description" />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Features (Optional){" "}
                                            <Info 
                                                id="onetimeproduct-features-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="onetimeproduct-features-info">
                                            List the key features included. Enter a feature and press Enter or click Add.
                                        </UncontrolledTooltip>
                                        <InputGroup>
                                            <Input
                                                type="text"
                                                value={oneTimeProductFeaturesInput}
                                                onChange={(e) => setOneTimeProductFeaturesInput(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        if (oneTimeProductFeaturesInput.trim()) {
                                                            const newFeatures = [...(oneTimeProductDetails.features || []), oneTimeProductFeaturesInput.trim()];
                                                            setOneTimeProductDetails({ ...oneTimeProductDetails, features: newFeatures });
                                                            setOneTimeProductFeaturesInput("");
                                                        }
                                                    }
                                                }}
                                                placeholder="Enter a feature and press Enter"
                                            />
                                            <Button color="primary" onClick={() => {
                                                if (oneTimeProductFeaturesInput.trim()) {
                                                    const newFeatures = [...(oneTimeProductDetails.features || []), oneTimeProductFeaturesInput.trim()];
                                                    setOneTimeProductDetails({ ...oneTimeProductDetails, features: newFeatures });
                                                    setOneTimeProductFeaturesInput("");
                                                }
                                            }}>
                                                Add
                                            </Button>
                                        </InputGroup>
                                        {oneTimeProductDetails.features && oneTimeProductDetails.features.length > 0 && (
                                            <div className="mt-2" style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                                                {oneTimeProductDetails.features.map((feature: string, index: number) => (
                                                    <Badge key={index} color="primary" className="d-inline-flex align-items-center" style={{ fontSize: "0.875rem", padding: "0.5rem", maxWidth: "100%", wordBreak: "break-word", whiteSpace: "normal", textAlign: "left", justifyContent: "flex-start" }}>
                                                        <span style={{ maxWidth: "calc(100% - 20px)", wordBreak: "break-word", textAlign: "left" }}>{feature}</span>
                                                        <X
                                                            size={14}
                                                            className="ms-2"
                                                            style={{ cursor: "pointer", flexShrink: 0 }}
                                                            onClick={() => {
                                                                const currentFeatures = oneTimeProductDetails.features || [];
                                                                const newFeatures = currentFeatures.filter((_: string, i: number) => i !== index);
                                                                setOneTimeProductDetails({ ...oneTimeProductDetails, features: newFeatures });
                                                            }}
                                                        />
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                {oneTimeProductDetails && oneTimeProductDetails.id ? (
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <label className="form-label">Filters</label>
                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => { toggleOneTimeProductModal(); openProductFiltersModal(oneTimeProductDetails, "onetimeproduct"); }}
                                                >
                                                    Filters
                                                </button>
                                            </div>
                                        </div>
                                    </Col>
                                ) : (
                                    <>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Categories <span className="text-danger">*</span>
                                                </label>
                                                <CategoryMultiSelect
                                                    selectedIds={oneTimeProductDetails.categories || []}
                                                    maxVisibleItems={4}
                                                    onChange={(ids) => setOneTimeProductDetails({ ...oneTimeProductDetails, categories: ids })}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <label className="form-label">Partners</label>
                                                <PartnerMultiSelect
                                                    selectedPartners={oneTimeProductDetails.partners || []}
                                                    maxVisibleItems={4}
                                                    onChange={(partners) => setOneTimeProductDetails({ ...oneTimeProductDetails, partners })}
                                                />
                                                <small className="form-text text-muted">
                                                    Optional. If none are selected, this product is not assigned to any partner. {partnerPickNote}
                                                </small>
                                            </div>
                                        </Col>
                                    </>
                                )}
                                <Col lg={12}>
                                    <div className="mb-3 form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="onetimeproduct-quantifiable"
                                            checked={!!oneTimeProductDetails.quantifiable}
                                            onChange={e => setOneTimeProductDetails({ ...oneTimeProductDetails, quantifiable: e.target.checked })}
                                        />
                                        <label className="form-check-label" htmlFor="onetimeproduct-quantifiable">
                                            Quantifiable{" "}
                                            <Info 
                                                id="onetimeproduct-quantifiable-info-icon" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="onetimeproduct-quantifiable-info-icon">
                                            Gives user ability to choose quantity of the product
                                        </UncontrolledTooltip>
                                    </div>
                                </Col>
                                <Col lg={6}>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Cancellation Days{" "}
                                            <Info 
                                                id="onetimeproduct-cancellation-days-info" 
                                                size={16} 
                                                style={{ cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle' }} 
                                            />
                                        </label>
                                        <UncontrolledTooltip placement="top" target="onetimeproduct-cancellation-days-info">
                                            Number of days after purchase when this product can be cancelled or refunded
                                        </UncontrolledTooltip>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={oneTimeProductDetails.cancellation_days ?? 5}
                                            onChange={e => {
                                                const val = parseInt(e.target.value);
                                                setOneTimeProductDetails({ ...oneTimeProductDetails, cancellation_days: isNaN(val) ? 5 : val });
                                            }}
                                            placeholder="5"
                                            min="0"
                                        />
                                    </div>
                                </Col>
                                <Col lg={12}>
                                    <div className="d-flex justify-content-end">
                                        <button className="btn btn-primary mb-3" type="button" onClick={async () => {
                                            const ok = oneTimeProductDetails && oneTimeProductDetails.id
                                                ? await handleOneTimeProduct("update")
                                                : await handleOneTimeProduct("create");
                                            if (ok) toggleOneTimeProductModal();
                                        }}>{oneTimeProductDetails && oneTimeProductDetails.id ? "Update One-Time Product" : "Add One-Time Product"}</button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </Modal>

                {/* Delete One-Time Product Modal */}
                <Modal isOpen={deleteOneTimeProductModal} toggle={toggleDeleteOneTimeProductModal} centered className="rounded-4">
                    <ModalHeader className="py-3 px-4" toggle={toggleDeleteOneTimeProductModal}>
                        <div className="modal-title mt-0 font-size-16 text-dark">Delete One-Time Product</div>
                    </ModalHeader>
                    <div className="modal-body">
                        <p>Are you sure you want to delete this one-time product?</p>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-sm btn-soft-dark" onClick={toggleDeleteOneTimeProductModal}>Cancel</button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => { handleOneTimeProduct("delete"); toggleDeleteOneTimeProductModal(); }}>Delete</button>
                    </div>
                </Modal>

                {/* Product Filters Modal */}
                <Modal isOpen={productFiltersModal} toggle={toggleProductFiltersModal} centered className="rounded-4" size="md" backdrop={'static'}>
                    <ModalHeader className="py-3 px-4" toggle={toggleProductFiltersModal}>
                        <div className="modal-title mt-0 font-size-16 text-dark">
                            Filters{productFiltersDetails?.title ? ` - ${productFiltersDetails.title}` : ""}
                        </div>
                    </ModalHeader>
                    <div className="modal-body">
                        <Row>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <label className="form-label">Categories</label>
                                    <CategoryMultiSelect
                                        selectedIds={productFiltersDetails.categories || []}
                                        maxVisibleItems={4}
                                        onChange={(ids) => setProductFiltersDetails({ ...productFiltersDetails, categories: ids })}
                                    />
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <label className="form-label">Partners</label>
                                    <PartnerMultiSelect
                                        selectedPartners={productFiltersDetails.partners || []}
                                        maxVisibleItems={4}
                                        onChange={(partners) => setProductFiltersDetails({ ...productFiltersDetails, partners })}
                                    />
                                    <small className="form-text text-muted">
                                        Optional. If none are selected, this product is not assigned to any partner. {partnerPickNote}
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-sm btn-soft-dark" onClick={toggleProductFiltersModal}>Cancel</button>
                        <button type="button" className="btn btn-sm btn-primary" onClick={saveProductFilters}>Save Filters</button>
                    </div>
                </Modal>

                {/* Manage Categories Modal */}
                <Modal isOpen={manageCategoriesModal} toggle={toggleManageCategoriesModal} centered className="rounded-4" size="lg">
                    <ModalHeader className="py-3 px-4" toggle={toggleManageCategoriesModal}>
                        <div className="modal-title mt-0 font-size-16 text-dark">Manage Categories</div>
                    </ModalHeader>
                    <div className="modal-body">
                        <Row>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <label className="form-label">Category Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={categoryDetails.name || ""}
                                        onChange={(e) => setCategoryDetails({ ...categoryDetails, name: e.target.value })}
                                        placeholder="Enter category name"
                                        disabled={!!categoryDetails.is_default}
                                    />
                                    {categoryDetails.is_default && (
                                        <small className="form-text text-muted">Default category name is protected.</small>
                                    )}
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        value={categoryDetails.description || ""}
                                        onChange={(e) => setCategoryDetails({ ...categoryDetails, description: e.target.value })}
                                        placeholder="Enter description (optional)"
                                        rows={2}
                                    />
                                </div>
                            </Col>
                            <Col lg={12} className="d-flex justify-content-end gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-soft-dark"
                                    onClick={() => setCategoryDetails({})}
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={upsertCategory}
                                >
                                    {categoryDetails?.id ? "Update Category" : "Add Category"}
                                </button>
                            </Col>
                        </Row>

                        <hr />

                        <div style={{maxHeight: 320, overflowY: 'auto'}}>
                            <table className="table table-sm table-striped align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th style={{width: 120}}>Priority</th>
                                        <th style={{width: 120}}>Default</th>
                                        <th style={{width: 160}}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map((cat: any) => {
                                        const catId = Number(cat.id);
                                        const movableIds = categories
                                            .filter((c: any) => !c?.is_default)
                                            .map((c: any) => Number(c.id));
                                        const idx = movableIds.indexOf(catId);
                                        const isMovable = !cat?.is_default && idx !== -1;
                                        const canMoveUp = isMovable && idx > 0;
                                        const canMoveDown = isMovable && idx < movableIds.length - 1;
                                        return (
                                        <tr key={cat.id}>
                                            <td>
                                                <div style={{fontWeight: 600}}>{cat.name}</div>
                                                {!!cat.description && (
                                                    <div className="text-muted" style={{fontSize: 12}}>
                                                        {cat.description}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div style={{display: 'flex', flexDirection: 'column'}}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0"
                                                            style={{color: canMoveUp ? '#1976d2' : '#ccc', lineHeight: 1}}
                                                            disabled={!canMoveUp}
                                                            onClick={() => moveCategoryPriority(catId, 'up')}
                                                            title="Move up"
                                                        >
                                                            <i className="bx bx-chevron-up" style={{fontSize: 16}}></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0"
                                                            style={{color: canMoveDown ? '#1976d2' : '#ccc', lineHeight: 1}}
                                                            disabled={!canMoveDown}
                                                            onClick={() => moveCategoryPriority(catId, 'down')}
                                                            title="Move down"
                                                        >
                                                            <i className="bx bx-chevron-down" style={{fontSize: 16}}></i>
                                                        </button>
                                                    </div>
                                                    <span>{cat.priority ?? 0}</span>
                                                </div>
                                            </td>
                                            <td>{cat.is_default ? "Yes" : "No"}</td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    {!cat.is_default && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => setCategoryDetails(cat)}
                                                        >
                                                            Edit
                                                        </button>
                                                    )}
                                                    {!cat.is_default && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => { setCategoryDetails(cat); toggleDeleteCategoryModal(); }}
                                                        >
                                                            Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                    {categories.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted">No categories found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Modal>

                {/* Delete Category Modal */}
                <Modal isOpen={deleteCategoryModal} toggle={toggleDeleteCategoryModal} centered className="rounded-4">
                    <ModalHeader className="py-3 px-4" toggle={toggleDeleteCategoryModal}>
                        <div className="modal-title mt-0 font-size-16 text-dark">Delete Category</div>
                    </ModalHeader>
                    <div className="modal-body">
                        <p>Are you sure you want to delete this category?</p>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-sm btn-soft-dark" onClick={toggleDeleteCategoryModal}>Cancel</button>
                        {!categoryDetails?.is_default && (
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => { deleteCategory(); toggleDeleteCategoryModal(); }}>Delete</button>
                        )}
                    </div>
                </Modal>

                {/* Manage Partners Modal */}
                <Modal isOpen={managePartnersModal} toggle={toggleManagePartnersModal} centered className="rounded-4" size="lg">
                    <ModalHeader className="py-3 px-4" toggle={toggleManagePartnersModal}>
                        <div className="modal-title mt-0 font-size-16 text-dark">Manage Partners</div>
                    </ModalHeader>
                    <div className="modal-body">
                        <div style={{maxHeight: 400, overflowY: 'auto'}}>
                            <table className="table table-sm table-striped align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Partner Name</th>
                                        <th style={{width: 120}}>Priority</th>
                                        <th style={{width: 100}}>Email</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {partners.map((partner: any, idx: number) => {
                                        const partnerId = String(partner.id);
                                        const canMoveUp = idx > 0;
                                        const canMoveDown = idx < partners.length - 1;
                                        return (
                                        <tr key={partner.id}>
                                            <td>
                                                <div style={{fontWeight: 600}}>{partner.name || `Partner #${partner.id}`}</div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div style={{display: 'flex', flexDirection: 'column'}}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0"
                                                            style={{color: canMoveUp ? '#1976d2' : '#ccc', lineHeight: 1}}
                                                            disabled={!canMoveUp}
                                                            onClick={() => movePartnerPriority(partnerId, 'up')}
                                                            title="Move up"
                                                        >
                                                            <i className="bx bx-chevron-up" style={{fontSize: 16}}></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0"
                                                            style={{color: canMoveDown ? '#1976d2' : '#ccc', lineHeight: 1}}
                                                            disabled={!canMoveDown}
                                                            onClick={() => movePartnerPriority(partnerId, 'down')}
                                                            title="Move down"
                                                        >
                                                            <i className="bx bx-chevron-down" style={{fontSize: 16}}></i>
                                                        </button>
                                                    </div>
                                                    <span>{idx + 1}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="text-muted" style={{fontSize: 12}}>
                                                    {partner.email || '—'}
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                    {partners.length === 0 && (
                                        <tr>
                                            <td colSpan={3} className="text-center text-muted">No partners found for this domain.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Modal>

            </div>
        </div>
    </>;
}